import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import Axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiConfig, shouldLogApiRequests } from '../config/api';
import {
  eventKey,
  eventParticipationKey,
  type EventsPov,
  type EventViewParam,
  eventsListKey,
  eventsTotalsKey,
  featuredEventsListKey,
  myCheckedInEventsKey,
  resolveEventKey,
} from '@lib/cache/events';
import {
  questionsMeetverseKey,
  questionsParamsKey,
  questionDetailKey,
  userQuestionsKey,
  questionInteractionsKey,
  questionUpliftUsersKey,
  interactionsKey,
  meTooInteractionKey,
  iCanHelpInteractionKey,
  questionTotalsKey,
} from '@lib/cache/questions';
import { userProfileKey, usersListKey, resolveProfileKey } from '@lib/cache/users';
import { featuredSpacesListKey } from '@lib/cache/spaces';
import { answersListKey } from '@lib/cache/answers';
import { getAmplitudeDeviceId } from '@lib/amplitude';
import { maybeProbe, getState as getMaintenanceState } from '@lib/maintenance';
import { logger } from '@utils/logger';
import { DEVICE_TIMEZONE_HEADER, deviceTimezoneForHeader } from '@utils/timezone';
import { tapWeMetV2Row, tapWeMetV2Rows } from './weMetEnrichment';
import { isNetworkError as isLikelyNetworkError, isRetryLaterAnswer } from '@utils/errorDetection';
import { getMsUntilTokenExpiry } from '@utils/auth';
import { recordTokenRefreshFailure } from '@utils/authRecovery';
import type { UserProfileApiResponse, UserProfileResponse } from '@models';
import type {
  BlockCheckResponse,
  BlockedUserResponse,
  CheckInRequest,
  CheckInResponse,
  AdminReportsResponse,
  ContentReportAdminRead,
  ContentReportCreate,
  ContentReportRead,
  ContentReportStatusUpdate,
  ReportStatus,
  EventFeedCountsResponse,
  IcpSearchRequest,
  IcpSearchResponse,
  InteractionToggleResponse,
  NotificationListResponse,
  NotificationUnreadCountResponse,
  PaginatedResponse,
  MagicLinkAccepted,
  MagicLinkRequest,
  PendingQuestionCreate,
  PendingQuestionRead,
  QuestionDetailRead,
  QuestionInteractionsResponse,
  ShareQuestionToSpacesRequest,
  ShareQuestionToSpacesResponse,
  RankedListMeta,
  SignupFlowData,
  PushMigrationStatusResponse,
  PushSubscriptionCreate,
  PushSubscriptionRead,
  UserBlockCreate,
  UserBlockRead,
  UserDeleteResponse,
  UserParticipationStatus,
  EventAttendeeRead,
  VAPIDPublicKeyResponse,
  CommunityRead,
  CommunityCreate,
  CommunityUpdate,
  SpaceCategory,
  SpaceMemberRead,
  SpaceMembersListMeta,
  BulkApproveMembersBody,
  BulkApproveMembersResult,
  SpaceMemberStatus,
  SpacePageRead,
  SpacePageUpdate,
  NotificationTemplateCreate,
  NotificationTemplateRead,
  NotificationTemplateUpdate,
  NotificationSettingsRead,
  NotificationSettingsUpdate,
  NotificationConfigHistoryPage,
  ConfigRevertRequest,
  NotificationTopicsResponse,
  PartnerListingCreate,
  PartnerListingCreated,
  PartnerListingRead,
  PartnerListingReorder,
  PartnerListingUpdate,
  PartnerListingsResponse,
  PartnerStatsAccepted,
  PartnerStatsBatch,
  SpaceInviteBulkRequest,
  SpaceInviteBulkResponse,
  SpaceInvitationActionRequest,
  SpaceInvitationDetails,
  SpaceMemberRole,
  SpaceExternalCalendarCreate,
  SpaceExternalCalendarRead,
  SpaceExternalCalendarUpdate,
  DuplicatePair,
  DuplicatePairAnswer,
  DuplicatePairAnswerResult,
  EventMatch,
  EventMatchCandidatesRequest,
  SelfReportedRegistrationRequest,
  SelfReportedRegistrationResponse,
} from '@models/api';
import type { UUID } from '@models/api/shared';

declare module 'axios' {
  interface AxiosRequestConfig {
    _retry?: boolean;
    _sessionExpiredHandled?: boolean; // AIDEV-NOTE: Flag to prevent retry loops on session expiration
  }
}

// Global bearer token accessor - set via AuthProvider
let globalGetAuthToken: (() => Promise<string | null>) | null = null;

// AIDEV-NOTE: Global flag to block all token requests when session is expired
// This is set by AuthProvider when invalid_grant is detected
let globalSessionExpired = false;

/**
 * Set the global bearer token getter function used for API authentication.
 */
export const setGlobalAuthTokenGetter = (
  getter: () => Promise<string | null>,
) => {
  globalGetAuthToken = getter;
};

/**
 * Mark the session as expired globally - blocks all token requests
 * Called by AuthProvider when invalid_grant is detected
 */
export const setGlobalSessionExpired = (expired: boolean) => {
  globalSessionExpired = expired;
  if (expired) {
    logger.debug('🔒 API Client: Session marked as expired, blocking all token requests');
  } else {
    logger.debug('🔓 API Client: Session expired flag cleared');
  }
};

/**
 * Get the current auth token for non-axios consumers (e.g., SSE EventSource).
 * Returns null when no token getter is registered or the session is expired.
 *
 * Note: rejections from the underlying `globalGetAuthToken()` (e.g. Logto
 * fetch failures, network errors) propagate to the caller. This function
 * does not catch them. Callers running outside an axios interceptor must
 * wrap with `.catch(() => null)` if they want the "treat as no token"
 * semantics. `useEventStream` does this; future consumers should too.
 */
export const getAuthToken = async (): Promise<string | null> => {
  if (globalSessionExpired || !globalGetAuthToken) return null;
  return globalGetAuthToken();
};

/**
 * Check if session is globally expired
 */
export const isGlobalSessionExpired = (): boolean => globalSessionExpired;

/**
 * Shape of the client-side error thrown when a request is issued while the
 * session is expired. Exported so `authAwareRetry` in `src/index.tsx` and any
 * UI surface can detect the error without string-matching.
 */
export interface SessionExpiredError extends Error {
  code: 'ERR_SESSION_EXPIRED';
  isSessionExpired: true;
}

/**
 * Type guard for session-expired rejections produced by the request interceptor.
 */
export const isSessionExpiredError = (err: unknown): err is SessionExpiredError => {
  if (!err || typeof err !== 'object') return false;
  const e = err as Partial<SessionExpiredError>;
  return e.code === 'ERR_SESSION_EXPIRED' || e.isSessionExpired === true;
};

/**
 * Build the session-expired rejection used by the request interceptor.
 */
const createSessionExpiredError = (): SessionExpiredError => {
  const err = new Error('Session expired - request blocked') as SessionExpiredError;
  err.code = 'ERR_SESSION_EXPIRED';
  err.isSessionExpired = true;
  return err;
};

/**
 * Client-side error class thrown when a request is short-circuited because
 * the app is in maintenance mode. Exported so `authAwareRetry` in
 * `src/index.tsx` and the response interceptor below can detect the error
 * and skip retries / probe forwarding without string-matching.
 *
 * Class-based so consumers can narrow via `instanceof MaintenanceError`,
 * which is stricter than a duck-typed structural check (a third-party
 * library happening to set `code: 'ERR_MAINTENANCE'` on its own error
 * shapes won't accidentally pass the guard).
 */
export class MaintenanceError extends Error {
  readonly code = 'ERR_MAINTENANCE' as const;
  readonly isMaintenance = true as const;
  constructor(message = 'Service is in maintenance') {
    super(message);
    this.name = 'MaintenanceError';
    // Restore the prototype chain after super(). With this build's tsconfig
    // target = ES2020, native `class extends Error` honors `new.target` and
    // the chain is intact without this call - so it's defense-in-depth, not
    // a current necessity. The TypeScript handbook ("Extending built-ins
    // like Error") still recommends it because any future toolchain change
    // (target downshift to ES5/ES2015, ESBuild plugin rewriting class
    // semantics, downlevel-iteration helpers) would otherwise leave
    // `Object.getPrototypeOf(this) === Error.prototype` instead of
    // `MaintenanceError.prototype`, breaking `instanceof` checks silently.
    // Same-realm only: cross-realm `instanceof` (e.g. iframes) fails
    // regardless of this call because each realm has its own
    // `MaintenanceError.prototype` object.
    Object.setPrototypeOf(this, MaintenanceError.prototype);
  }
}

/**
 * Type guard for maintenance rejections produced by the request interceptor.
 * Uses `instanceof` so duck-typed objects (e.g., third-party errors with a
 * matching `code` field) cannot pass the check.
 */
export const isMaintenanceError = (err: unknown): err is MaintenanceError =>
  err instanceof MaintenanceError;

/**
 * Custom retry function that skips retries on 403 errors (access denied).
 * Used for question endpoints where guests may receive 403 for private questions.
 */
const skipRetryOn403 = (failureCount: number, error: Error): boolean => {
  const status = (error as { status?: number })?.status ??
    (error as { response?: { status?: number } })?.response?.status;
  if (status === 403) return false;
  return failureCount < 3;
};

/**
 * Configure axios instance with base URL and interceptors
 */
export const axiosInstance = Axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  // AIDEV-NOTE: Serialize arrays as repeated params (?event_ids=a&event_ids=b) per REST convention
  paramsSerializer: {
    indexes: null, // No brackets: event_ids=a&event_ids=b (not event_ids[]=a)
  },
});

// Enhanced request interceptor for authentication and debugging
axiosInstance.interceptors.request.use(
  async (config) => {
    // AIDEV-NOTE: Add Amplitude device ID header for session linking with backend analytics
    // This enables backend events to be linked to the same user session
    const deviceId = getAmplitudeDeviceId();
    if (deviceId) {
      config.headers = config.headers || {};
      config.headers['X-Amplitude-Device-Id'] = deviceId;
    }

    // AIDEV-NOTE: Issue #567 - If the session is globally expired, hard-reject the
    // request client-side instead of sending a tokenless request. Previously we
    // returned `config` here, which skipped the token but still dispatched the
    // request, producing backend 401s during the recovery window.
    if (globalSessionExpired) {
      logger.debug('🔒 API Request: Rejecting - session is expired');
      return Promise.reject(createSessionExpiredError());
    }

    // Short-circuit during maintenance state: fail fast instead of waiting
    // for timeouts or retries. The detector probe runs separately.
    //
    // startsWith (not includes): all axios callers build URLs from
    // apiConfig.baseURL + path where path always starts with '/api/v1/...'.
    // includes() would match a hypothetical '/foo/api/v1/bar' which no
    // caller produces, but startsWith is strictly tighter and the right
    // anchor for our call shape. The detector's bare fetch() probe targets
    // /health (top-level, not under /api/v1/) so it's naturally outside
    // this short-circuit's scope.
    if (getMaintenanceState().status === 'maintenance' && config.url?.startsWith('/api/v1/')) {
      return Promise.reject(new MaintenanceError());
    }

    // Use JWT ID token for all API requests (unified JWT authentication)
    if (globalGetAuthToken && !config.headers?.Authorization) {
      try {
        const bearerToken = await globalGetAuthToken();
        if (bearerToken) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${bearerToken}`;

          // AIDEV-NOTE: Check if token is expiring soon (within 2 minutes)
          // This is a warning for observability - the proactive refresh in AuthProvider
          // should prevent this from happening, but if it does, log it for debugging
          const msUntilExpiry = getMsUntilTokenExpiry(bearerToken);
          if (msUntilExpiry !== null && msUntilExpiry < 2 * 60 * 1000 && msUntilExpiry > 0) {
            logger.warn(`⚠️ Token expires in ${Math.round(msUntilExpiry / 1000)}s - proactive refresh may have failed`);
            // Don't block the request - backend typically has a grace period
            // The proactive refresh in AuthProvider will trigger soon
          }
        }
        // Note: Only log errors if token fetch fails during a retry; missing token is allowed for guest browsing
      } catch (error) {
        // Only log if this is a retry attempt (unexpected failure)
        if (config._retry) {
          logger.error('❌ Auth: Token refresh failed during retry:', error);
        }
        // Continue with request even if token fetch fails
        // The backend will handle unauthorized requests appropriately
      }
    }

    // AIDEV-NOTE: The device zone is personal data: only a request that carries a bearer sends it.
    // Reads Authorization itself: a caller's own bearer and the 401 replay skip the block above.
    if (config.headers.Authorization) {
      try {
        const deviceZone = deviceTimezoneForHeader();
        if (deviceZone) {
          config.headers[DEVICE_TIMEZONE_HEADER] = deviceZone;
        }
      } catch {
        // Reading the zone threw (a script or an extension broke Date or Intl): send without it.
      }
    }

    return config;
  },
  (error) => {
    logger.error('❌ API Request Error:', error as Error);
    return Promise.reject(error);
  },
);

// Enhanced response interceptor for token refresh and error handling
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // AIDEV-NOTE: Issue #567 - If the session flipped to expired while a request
    // was in-flight (flag set AFTER the request left the interceptor but BEFORE
    // the 401 came back), drop immediately. Any token-refresh attempt here would
    // race with the recovery flow in useInvalidGrantHandler.
    if (globalSessionExpired) {
      if (originalRequest) {
        originalRequest._sessionExpiredHandled = true;
      }
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized responses
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const hadAuthHeader = originalRequest.headers?.Authorization;

      // AIDEV-NOTE: If we already tried to refresh and still got 401, the session is expired
      // Don't keep retrying - AuthProvider will handle re-authentication
      if (originalRequest._sessionExpiredHandled) {
        logger.debug('401 received after session expiration handling - not retrying');
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (globalGetAuthToken) {
        try {
          // Try to get a fresh bearer token (access token preferred)
          const newToken = await globalGetAuthToken();

          // AIDEV-NOTE: If token getter returns null, session is likely expired or user is guest
          // Don't retry the request - if it was an authenticated request, it should fail
          if (newToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          } else {
            // No token available - mark as handled to prevent future retries
            originalRequest._sessionExpiredHandled = true;
            if (hadAuthHeader) {
              logger.debug('Token getter returned null on 401 - session may be expired or reauthenticating');
              // AIDEV-NOTE: Issue #567 follow-up - null from the token getter on
              // a previously-authed request is another token-fetch failure.
              // Bump the breaker; the getter in AuthProvider reads the same
              // counter and will escalate to handleInvalidGrant once tripped.
              recordTokenRefreshFailure();
            }
          }
        } catch (refreshError) {
          // Mark as handled to prevent retry loops
          originalRequest._sessionExpiredHandled = true;

          // Only log refresh failures if user was authenticated
          if (hadAuthHeader) {
            logger.error('❌ Auth: Token refresh failed:', refreshError);
            recordTokenRefreshFailure();
          }
          // Let the error fall through to trigger logout/redirect
        }
      }
    }

    if (shouldLogApiRequests) {
      // Filter out aborted requests and network errors as they're usually not real errors
      const isAbortedRequest = error.code === 'ERR_CANCELED' || error.message?.includes('aborted');
      const isNetworkError = error.code === 'ERR_NETWORK';
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const isRateLimit = error.response?.status === 429;
      // Skip expected 401s for guest users (no auth header) or during session expiration
      const is401WithoutAuth = error.response?.status === 401 && !originalRequest?.headers?.Authorization;
      const is401SessionExpired = error.response?.status === 401 && originalRequest?._sessionExpiredHandled;

      if (!isAbortedRequest && !isNetworkError && !is401WithoutAuth && !is401SessionExpired) {
        if (isRateLimit) {
          logger.warn('🚦 Rate Limited:', error.response?.data || 'Too many requests');
        } else if (isTimeout) {
          logger.warn(
            '⏱️ Request Timeout:',
            error.config?.url,
            '- Consider increasing timeout or checking backend performance',
          );
        } else {
          logger.error(
            '❌ API Response Error:',
            error.response?.status || 'Unknown Status',
            error.response?.data || error.message || 'Unknown Error',
          );
        }
      }
    }

    // Forward 5xx and network errors to the maintenance detector.
    // Non-blocking: detector decides asynchronously whether to flip state.
    //
    // Three shapes that look like "no response" but should NOT trigger a
    // maintenance probe:
    //   1. ERR_MAINTENANCE - our own request-interceptor short-circuit. The
    //      detector already knows we're in maintenance; re-probing on every
    //      blocked request floods the system.
    //   2. Axios cancellations (Axios.isCancel / ERR_CANCELED) - the user
    //      navigated away or a React unmount aborted the request. Not an
    //      outage signal.
    //   3. Generic Errors thrown from interceptors / queryFns - parse
    //      failures, assertions, app logic bugs. isLikelyNetworkError
    //      narrows to true network shapes (axios codes ERR_NETWORK /
    //      ECONNABORTED / ETIMEDOUT / ECONNRESET, fetch-level TypeErrors
    //      with cross-browser messages including Safari "Load failed").
    const status = error?.response?.status;
    // 503 ("Service Unavailable") is RFC 9110's explicit "scheduled
    // maintenance / temporary overload" code. Tier it separately from
    // generic 5xx so the detector's THRESHOLD_BY_REASON can flip the
    // takeover on a single corroborating /health failure rather than
    // requiring the standard 2-failure damping confirmation.
    //
    // Except a 503 the backend sends by design (isRetryLaterAnswer): a busy
    // database (DATABASE_BUSY) or a lock wait on the notification config
    // pages, answered in its own envelope.
    // The server is up, so it is no outage signal; forwarded, one /health
    // blip at that moment would raise the takeover over a live session.
    // queryCacheOnError skips it the same way.
    if (status === 503) {
      if (!isRetryLaterAnswer(error)) void maybeProbe('503');
    } else if (status && status >= 500) {
      void maybeProbe('5xx');
    } else if (
      !error?.response &&
      !isMaintenanceError(error) &&
      !Axios.isCancel(error) &&
      isLikelyNetworkError(error)
    ) {
      void maybeProbe('network-error');
    }

    return Promise.reject(error);
  },
);

/**
 * Custom axios instance for API calls
 */
export const customInstance = <T>(
  config: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return axiosInstance({
    ...config,
  });
};

// Type definitions for API models
export type AnswerCreate = import('@models/api').AnswerCreate;
export type AnswerRead = import('@models/api').AnswerRead;
export type AnswerUpdate = import('@models/api').AnswerUpdate;
export type EventCreate = import('@models/api').EventCreate;
export type EventRead = import('@models/api').EventRead;
export type EventUpdate = import('@models/api').EventUpdate;
export type EventImportPlatform = import('@models/api').EventImportPlatform;
export type EventImportRequest = import('@models/api').EventImportRequest;
export type EventImportResponse = import('@models/api').EventImportResponse;
export type AttendEventResponse = import('@models/api').AttendEventResponse;
export type AttendEventSpaceResult = import('@models/api').AttendEventSpaceResult;
export type PinRequiredSpace = import('@models/api').PinRequiredSpace;
export type SpaceEventApprovalResult = import('@models/api').SpaceEventApprovalResult;
export type EventParticipantCreate = import('@models/api').EventParticipantCreate;
export type EventParticipantRead = import('@models/api').EventParticipantRead;
export type EventParticipantUpdate = import('@models/api').EventParticipantUpdate;
export type InteractionCreate = import('@models/api').InteractionCreate;
export type InteractionRead = import('@models/api').InteractionRead;
export type InteractionsResponse = import('@models/api').InteractionsResponse;
export type QuestionCreate = import('@models/api').QuestionCreate;
export type QuestionRead = import('@models/api').QuestionRead;
export type QuestionProfileRead = import('@models/api').QuestionProfileRead;
export type QuestionUpdate = import('@models/api').QuestionUpdate;
export type SpaceTopic = import('@models/api').SpaceTopic;
export type SpaceTopicCreate = import('@models/api').SpaceTopicCreate;
export type SpaceTopicUpdate = import('@models/api').SpaceTopicUpdate;
export type SpaceTopicReorderItem = import('@models/api').SpaceTopicReorderItem;
export type PinResponse = import('@models/api').PinResponse;
export type PinErrorCode = import('@models/api').PinErrorCode;
export type PinErrorDetail = import('@models/api').PinErrorDetail;
export type UserRead = import('@models/api').UserRead;
export type UserUpdate = import('@models/api').UserUpdate;
export type WeMetConnectionCreate = import('@models/api').WeMetConnectionCreate;
export type WeMetConnectionRead = import('@models/api').WeMetConnectionRead;
export type SuggestedHelper = import('@models/api').SuggestedHelper;
export type SuggestedHelpersResponse = import('@models/api').SuggestedHelpersResponse;
export type HelpableQuestionsResponse = import('@models/api').HelpableQuestionsResponse;
export type HelpableUnseenCount = import('@models/api').HelpableUnseenCount;
export type EventHelpableResponse = import('@models/api').EventHelpableResponse;

/**
 * Fetch a single user profile by ID from the API
 * @param userId - The unique identifier for the user
 * @returns Promise resolving to the user profile data
 */
export const fetchUserProfile = async (
  userId: string,
): Promise<UserProfileResponse> => {
  if (!userId || userId.trim() === '') {
    throw new Error('User ID is required');
  }

  try {
    const response = await customInstance<UserProfileResponse>({
      url: `/api/v1/users/${encodeURIComponent(userId)}`,
      method: 'GET',
    });

    return response.data;
  } catch (error: any) {
    // Enhanced error handling with specific error messages
    if (error?.response?.status === 404) {
      throw new Error('User profile not found');
    } else if (error?.response?.status === 403) {
      throw new Error('Access denied to user profile');
    } else if (error?.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error?.response?.status >= 500) {
      throw new Error('Server error occurred while fetching profile');
    } else if (!navigator.onLine) {
      throw new Error('No internet connection');
    } else {
      throw new Error(error?.message || 'Failed to fetch user profile');
    }
  }
};

export const resolveProfileBySlug = (
  slug: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserRead>> => {
  return customInstance<UserRead>({
    url: `/api/v1/resolve/profiles/${encodeURIComponent(slug)}`,
    method: 'GET',
    ...options,
  });
};

/**
 * Admin-only: Meetball members who share a space with a question's author,
 * ranked by profile match. Internal half of the suggested-helpers feature.
 */
export const getSuggestedHelpers = (
  questionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SuggestedHelpersResponse>> => {
  return customInstance<SuggestedHelpersResponse>({
    url: `/api/v1/questions/${encodeURIComponent(questionId)}/suggested-helpers`,
    method: 'GET',
    ...options,
  });
};

/**
 * Paginated helper feed: questions the current user can help with, ranked by
 * how well they match the user's intents. Backs the helper-feed surface.
 */
export const getHelpableQuestions = (
  params: { page?: number; per_page?: number; cursor?: number; anchor?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<HelpableQuestionsResponse>> => {
  return customInstance<HelpableQuestionsResponse>({
    url: '/api/v1/questions/helpable',
    method: 'GET',
    params: params,
    ...options,
  });
};

/**
 * Unseen count for the helper feed, backing the entry-point badge.
 */
export const getHelpableCount = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<HelpableUnseenCount>> => {
  return customInstance<HelpableUnseenCount>({
    url: '/api/v1/questions/helpable/count',
    method: 'GET',
    ...options,
  });
};

/**
 * Mark a helpable question as opened (seen) by the current user, clearing it
 * from the unseen count.
 */
export const markHelpableOpened = (
  questionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/questions/${encodeURIComponent(questionId)}/helpable/open`,
    method: 'POST',
    ...options,
  });
};

/**
 * Mark that the asker (the question's author OR the beneficiary it was asked for)
 * has SEEN their helpers, advancing the server-side seen-at watermark used to
 * suppress duplicate "you have helpers" pushes. Mirror-image of
 * `markHelpableOpened` (the helper-side open ping). Idempotent (LAST-view wins).
 */
export const markHelpersSeen = (
  questionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/questions/${encodeURIComponent(questionId)}/helpers-seen`,
    method: 'POST',
    ...options,
  });
};

/**
 * Dismiss a helpable question for the current user, removing it from their
 * helper feed.
 */
export const dismissHelpable = (
  questionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/questions/${encodeURIComponent(questionId)}/helpable/dismiss`,
    method: 'POST',
    ...options,
  });
};

/**
 * Viewer-scoped helpable-at-event read (Track 2 Phase A): the matcher's
 * already-judged matches where the viewer is the helper, intersected with the
 * event's post set. Serves any viewable, non-deleted event on ANY phase
 * (upcoming / live / ended - feed parity with the event feed, no live-window
 * gate); one fetch backs the "For you" section cards, the live pill's count,
 * and the feed chips. Accepts a slug OR a UUID (backend resolves either).
 * `enabled: false` = the backend lever is off (a client fetch error is the
 * equivalent silent no-render) -> `{ enabled: false, total: 0, items: [] }`.
 */
export const getEventHelpable = (
  eventIdOrSlug: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventHelpableResponse>> => {
  return customInstance<EventHelpableResponse>({
    url: `/api/v1/events/${encodeURIComponent(eventIdOrSlug)}/helpable`,
    method: 'GET',
    ...options,
  });
};

/**
 * Get user profile by user ID
 * Uses JWT ID token authentication via interceptor
 * @param userId - The user ID to fetch
 * @returns Promise resolving to the user profile
 */
export const getUserProfileById = async (
  userId: string,
): Promise<UserProfileApiResponse> => {
  try {
    // JWT ID token authentication handled by interceptor
    const response = await customInstance<UserProfileApiResponse>({
      url: `/api/v1/users/${userId}`,
      method: 'GET',
    });

    return response.data;
  } catch (error) {
    logger.error('Failed to fetch user profile by ID:', error as Error);
    throw error;
  }
};

/**
 * Get current user profile for authenticated user
 * Uses JWT ID token authentication via interceptor
 * @returns Promise resolving to the user profile
 */
export const getCurrentUserProfile = async (): Promise<UserProfileResponse> => {
  try {
    // GET /api/v1/users/me now returns the full user profile directly
    // JWT ID token authentication handled by interceptor
    const response = await customInstance<UserProfileResponse>({
      url: `/api/v1/users/me`,
      method: 'GET',
    });

    return response.data;
  } catch (error) {
    logger.error('Failed to fetch current user profile:', error as Error);
    throw error;
  }
};

/**
 * Get or create current user from Logto authentication data (for signup flow)
 * @param logtoData - User data from Logto authentication
 * @returns Promise resolving to the backend user profile
 */
export const signUpAndfetchCurrentUser = async (
  logtoData: SignupFlowData,
): Promise<UserProfileApiResponse> => {
  if (!logtoData.auth_id) {
    throw new Error('Logto sub (user ID) is required');
  }

  try {
    // Prepare request body with user data
    const requestBody: SignupFlowData = {
      step1: logtoData.step1,
      step2: logtoData.step2,
      step3: logtoData.step3,
      auth_id: logtoData.auth_id,
      email: logtoData.email,
      profile_picture: logtoData.profile_picture || null,
      auth_provider: logtoData.auth_provider || 'unknown',
      referred_by_code: logtoData.referred_by_code,
      attribution: logtoData.attribution,
    };

    // JWT ID token authentication handled by interceptor
    const response = await customInstance<UserProfileApiResponse>({
      url: `/api/v1/users/me`,
      method: 'POST',
      data: requestBody,
    });

    return response.data;
  } catch (error: any) {
    // Enhanced error handling
    if (error?.response?.status === 400) {
      throw new Error('Invalid user data provided');
    } else if (error?.response?.status === 500) {
      throw new Error('Server error occurred while creating/fetching user');
    } else if (!navigator.onLine) {
      throw new Error('No internet connection');
    } else {
      throw new Error(error?.message || 'Failed to get current user');
    }
  }
};

// The fields a person may edit on their own profile: the backend's allow-list,
// `USER_EDITABLE_FIELDS` in `app/api/api_v1/endpoints/users.py`. The profile PUT
// refuses any other key in the body with a 403 "Restricted fields cannot be
// updated": the lifecycle fields (`is_active`, `last_active_at`, `deactivated_at`)
// and the referral fields (`referral_code`, `is_champion`, ...). `SafeUserPatch` is
// built from this list, and the runtime strip in `patchCurrentUserProfile` keeps
// only these keys in case an untyped object (`as any`, a `JSON.parse` result)
// slips past the type. Keep it in step with the backend's list.
const EDITABLE_PROFILE_FIELDS = [
  'slug',
  'linkedin_url',
  'full_name',
  'profile_picture',
  'cover_image_url',
  'bio',
  'fields_of_expertise',
  'professional_background',
  'can_help_with',
  'interests',
  'personality_traits',
  'skills',
] as const satisfies readonly (keyof UserUpdate)[];

// Set form for the runtime filter: `.includes(key)` on the literal tuple would
// not accept a plain `string` key.
const EDITABLE_PROFILE_FIELD_SET: ReadonlySet<string> = new Set(EDITABLE_PROFILE_FIELDS);

/**
 * The shape every consumer of `patchCurrentUserProfile` (and its hook wrapper
 * `usePatchCurrentUserProfile`) should construct: only the fields being changed,
 * from the fields a person may edit. Lifecycle and referral fields are not in
 * the type, so callers can't ask for them. `full_name` can't be null: the column
 * is NOT NULL, so a null would fail in the database instead of as a 422.
 */
export type SafeUserPatch = Omit<
  Pick<UserUpdate, (typeof EDITABLE_PROFILE_FIELDS)[number]>,
  'full_name'
> & { full_name?: string };

/**
 * Partial update for the current user: a PUT to /users/{id} whose body is
 * exactly `patch`, minus keys a person may not edit and `undefined` values. An
 * explicit `null` is sent, and clears that field.
 *
 * The backend applies PATCH semantics to this PUT: `update_user` dumps the body
 * with `exclude_unset=True`, so a field the body leaves out keeps its stored
 * value. Never fill the body from a cached user. The cache can be older than the
 * row (another device saved since), so a re-sent field reverts that save, and a
 * stale image URL off the backend's image allow-list fails the whole save with a
 * 422 on a field the person never touched (TECH-689).
 */
export const patchCurrentUserProfile = async (
  userId: string,
  patch: SafeUserPatch,
): Promise<UserProfileResponse> => {
  // Defensive strip. `SafeUserPatch` already admits only editable fields, so
  // this is belt-and-braces: it drops any other key when a patch crosses an
  // untyped boundary (e.g. `as any`) and the type guarantee is bypassed. Cheap
  // to keep: O(n) over the patch's own keys.
  const safePatch: SafeUserPatch = Object.fromEntries(
    Object.entries(patch).filter(
      ([key, value]) => value !== undefined && EDITABLE_PROFILE_FIELD_SET.has(key),
    ),
  );

  const response = await customInstance<UserProfileResponse>({
    url: `/api/v1/users/${userId}`,
    method: 'PUT',
    data: safePatch,
  });

  return response.data;
};

/**
 * React Query hook for fetching user profile data
 * @param userId - The unique identifier for the user
 * @param options - Optional React Query configuration
 * @returns React Query result with user profile data, loading state, and error handling
 */
export const useUserProfile = (
  userId: string | undefined,
  options?: Omit<
    UseQueryOptions<UserProfileResponse, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<UserProfileResponse, Error>({
    queryKey: userProfileKey(userId),
    queryFn: () => {
      if (!userId) {
        throw new Error('User ID is required');
      }
      return fetchUserProfile(userId);
    },
    enabled: !!userId && userId.trim() !== '', // Only run query if userId is provided and not empty
    // staleTime / gcTime intentionally NOT overridden here — they
    // inherit from the root `QueryClient` (`staleTime: 5 * 60 * 1000`
    // in src/index.tsx) + TanStack's default `gcTime: 5 * 60 * 1000`.
    //
    // The previous values (`staleTime: 0, gcTime: 0`) forced a full
    // refetch + skeleton every time the user navigated back to a
    // profile, because `gcTime: 0` destroyed the cache entry the
    // moment the last subscriber unmounted. Freshness is already
    // guaranteed by explicit invalidations on mutations that change
    // the profile:
    //   - ProfileEditFlow.tsx (save)
    //   - usePatchCurrentUserProfile.ts (avatar and cover patches)
    // ...so the cache can safely survive navigation without going
    // out of sync. If callers need a different policy, they can pass
    // `staleTime` / `gcTime` through `options` — the spread below
    // still wins.
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (user not found) or 403 (access denied)
      if (
        error?.message?.includes('not found') ||
        error?.message?.includes('Access denied')
      ) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    ...options,
  });
};

// React Query hooks for resolver endpoints
export const useResolveEventBySlug = (
  slug: string,
  options?: Omit<
    UseQueryOptions<AxiosResponse<EventRead>, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: resolveEventKey(slug),
    queryFn: () => resolveEventBySlug(slug),
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useResolveProfileBySlug = (
  slug: string,
  options?: Omit<
    UseQueryOptions<AxiosResponse<UserRead>, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: resolveProfileKey(slug),
    queryFn: () => resolveProfileBySlug(slug),
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

// API function signatures
export const readEventsApiV1EventsGet = (
  params?: {
    page?: number;
    per_page?: number;
    user_id?: string;
    pov?: EventsPov;
    search?: string;
    view?: EventViewParam;
  },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventRead[]>> => {
  return customInstance<EventRead[]>({
    url: '/api/v1/events',
    method: 'GET',
    params,
    ...options,
  });
};

export const readEventsTotalsApiV1EventsTotalsGet = (
  params?: { pov?: EventsPov; user_id?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<{ upcoming: number; live: number; past: number; total: number }>> => {
  return customInstance<{ upcoming: number; live: number; past: number; total: number }>({
    url: '/api/v1/events/totals',
    method: 'GET',
    params,
    ...options,
  });
};

/**
 * Universal feed-aligned posts counts for a batch of events (T-8 ruling,
 * revised 2026-07-12: the count is the event's universal activity count —
 * the same number for guest, authed non-member, and member; membership
 * unlocks reading the posts, not the number). ≤ 30 ids per call; PUBLIC —
 * no auth required, guests fetch it too. Events the viewer cannot SEE
 * (per-viewer event visibility, e.g. unlisted/private) are OMITTED from
 * `counts`.
 *
 * Wire form: the ids ride as an ARRAY so the instance-level
 * `paramsSerializer: { indexes: null }` emits FastAPI's repeated-param shape
 * (`?event_ids=a&event_ids=b`) — never comma-joined, never bracketed
 * (`event_ids[]=`), neither of which FastAPI's `Query` list parsing reads.
 */
export const getEventFeedCounts = (
  eventIds: readonly string[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventFeedCountsResponse>> => {
  return customInstance<EventFeedCountsResponse>({
    url: '/api/v1/events/feed-counts',
    method: 'GET',
    params: { event_ids: eventIds },
    ...options,
  });
};

export const createEventApiV1EventsPost = (
  data: EventCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventRead>> => {
  return customInstance<EventRead>({
    url: '/api/v1/events',
    method: 'POST',
    data,
    ...options,
  });
};

export const importEventApiV1EventsImportPost = (
  data: EventImportRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventImportResponse>> => {
  return customInstance<EventImportResponse>({
    url: '/api/v1/events/import',
    method: 'POST',
    data,
    ...options,
  });
};

/** Events already on meetball that a person who is creating this event may be duplicating (at most 3). */
export const postEventMatchCandidates = (
  data: EventMatchCandidatesRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventMatch[]>> => {
  return customInstance<EventMatch[]>({
    url: '/api/v1/events/match-candidates',
    method: 'POST',
    data,
    ...options,
  });
};

export const readEventApiV1EventsEventIdGet = (
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventRead>> => {
  return customInstance<EventRead>({
    url: `/api/v1/events/${eventId}`,
    method: 'GET',
    ...options,
  });
};

// Resolver API functions - no manual auth headers needed (handled by interceptor)
export const resolveEventBySlug = (
  slug: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventRead>> => {
  return customInstance<EventRead>({
    url: `/api/v1/resolve/events/${encodeURIComponent(slug)}`,
    method: 'GET',
    ...options,
  });
};

export const getUserParticipationStatusApiV1EventsEventIdParticipationGet = (
  eventId: string,
  userId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserParticipationStatus>> => {
  return customInstance<UserParticipationStatus>({
    url: `/api/v1/events/${eventId}/participation`,
    method: 'GET',
    params: { user_id: userId },
    ...options,
  });
};

export const checkinToEventApiV1EventsEventIdCheckinPost = (
  eventId: string,
  userId: string,
  data: CheckInRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<CheckInResponse>> => {
  return customInstance<CheckInResponse>({
    url: `/api/v1/events/${eventId}/checkin`,
    method: 'POST',
    params: { user_id: userId },
    data,
    ...options,
  });
};


export const getMyCheckedInEventsApiV1EventsMyCheckedInGet = (
  includeMeetverse?: boolean,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventRead[]>> => {
  return customInstance<EventRead[]>({
    url: `/api/v1/events/my-checked-in`,
    method: 'GET',
    params: {
      ...(includeMeetverse !== undefined && {
        include_meetverse: includeMeetverse,
      }),
    },
    ...options,
  });
};

export const updateEventApiV1EventsEventIdPut = (
  eventId: string,
  data: EventUpdate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventRead>> => {
  return customInstance<EventRead>({
    url: `/api/v1/events/${eventId}`,
    method: 'PUT',
    data,
    ...options,
  });
};

export const deleteEventApiV1EventsEventIdDelete = (
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventUpdate>> => {
  return customInstance<EventUpdate>({
    url: `/api/v1/events/${eventId}`,
    method: 'DELETE',
    ...options,
  });
};

export const saveEventApiV1EventsEventIdSavePost = (
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/events/${eventId}/save`,
    method: 'POST',
    ...options,
  });
};

export const unsaveEventApiV1EventsEventIdSaveDelete = (
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/events/${eventId}/save`,
    method: 'DELETE',
    ...options,
  });
};

export const setEventGoingApiV1EventsEventIdGoingPost = (
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<AttendEventResponse>> => {
  // "Going" RSVPs AND auto-joins the event's spaces; the body reports each space's outcome
  // in joined/pending/skipped/failed buckets. See AttendEventResponse.
  return customInstance<AttendEventResponse>({
    url: `/api/v1/events/${eventId}/going`,
    method: 'POST',
    ...options,
  });
};

/** Releases the user's join hold on a PIN Space ("Request access"): the host is told now. */
export const releaseSpaceJoinHold = async (spaceId: UUID): Promise<void> => {
  await customInstance<void>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/join-hold`,
    method: 'DELETE',
  });
};

/** The PIN Spaces still join-held for the viewer on this event (reopens the PIN sheet). */
export const getEventJoinHolds = async (
  eventId: UUID,
): Promise<PaginatedResponse<PinRequiredSpace>> => {
  // Always a single page: the envelope is the backend's standard, the hook reads `.data`.
  const response = await customInstance<PaginatedResponse<PinRequiredSpace>>({
    url: `/api/v1/events/${encodeURIComponent(eventId)}/join-holds`,
    method: 'GET',
  });
  return response.data;
};

export const setSelfReportedRegistrationApiV1EventsEventIdSelfReportedRegistrationPut = (
  eventId: string,
  data: SelfReportedRegistrationRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SelfReportedRegistrationResponse>> => {
  // A Going member's own answer to the ticket question. A repeat call overwrites the answer.
  // 404: not Going / unknown event; 422: creator, ticket not required, or the event has ended.
  return customInstance<SelfReportedRegistrationResponse>({
    url: `/api/v1/events/${eventId}/self-reported-registration`,
    method: 'PUT',
    data,
    ...options,
  });
};

export const unsetEventGoingApiV1EventsEventIdGoingDelete = (
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/events/${eventId}/going`,
    method: 'DELETE',
    ...options,
  });
};

/**
 * Presence feed for an event (authed): questions in the event's spaces authored by
 * people who said they are Going. Returns the standard `PaginatedResponse` (offset
 * `pagination` plus a ranked `meta.next_cursor`); the infinite query prefers the cursor and
 * falls back to page. Same shape as the home spaces feed, so QuestionCard rendering is reused.
 * `sort` is reserved ('recent' only at launch).
 */
export const getEventFeed = (
  eventId: string,
  params?: { cursor?: number; page?: number; per_page?: number; sort?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<QuestionRead, RankedListMeta>>> => {
  return customInstance<PaginatedResponse<QuestionRead, RankedListMeta>>({
    url: `/api/v1/events/${eventId}/feed`,
    method: 'GET',
    params,
    ...options,
  });
};

/**
 * Going roster for an event (authed): the going-filtered attendee list ordered viewer, host,
 * host-Space admins, then most-recent Going,
 * with public user info, paginated. `search` is reserved for Slice 2.
 */
export const getEventAttendees = (
  eventId: string,
  params?: { cursor?: number; page?: number; per_page?: number; search?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<EventAttendeeRead, RankedListMeta>>> => {
  return customInstance<PaginatedResponse<EventAttendeeRead, RankedListMeta>>({
    url: `/api/v1/events/${eventId}/participants`,
    method: 'GET',
    params,
    ...options,
  });
};

export const readFeaturedEventsApiV1EventsFeaturedGet = (
  params?: { skip?: number; limit?: number },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventRead[]>> => {
  return customInstance<EventRead[]>({
    url: '/api/v1/events/featured',
    method: 'GET',
    params,
    ...options,
  });
};

/**
 * Get public events for guest users (unauthenticated).
 * AIDEV-NOTE: Used by guest question submission flow to populate event dropdown.
 */
export const readPublicEventsApiV1EventsPublicGet = (
  params?: { skip?: number; limit?: number; include_past?: boolean },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventRead[]>> => {
  return customInstance<EventRead[]>({
    url: '/api/v1/events/public',
    method: 'GET',
    params,
    ...options,
  });
};

export const claimEventApiV1EventsEventIdClaimPost = (
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/events/${eventId}/claim`,
    method: 'POST',
    ...options,
  });
};

export const updateEventFeaturedStatusApiV1EventsEventIdFeaturedPatch = (
  eventId: string,
  is_featured: boolean,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventRead>> => {
  return customInstance<EventRead>({
    url: `/api/v1/events/${eventId}/featured`,
    method: 'PATCH',
    params: { is_featured },
    ...options,
  });
};

export const setEventOwnerApiV1EventsEventIdSetOwnerPost = (
  eventId: string,
  data: { user_id: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventRead>> => {
  return customInstance<EventRead>({
    url: `/api/v1/events/${eventId}/set-owner`,
    method: 'POST',
    data,
    ...options,
  });
};

export const readQuestionsApiV1QuestionsGet = (
  params?: {
    cursor?: number;
    page?: number;
    per_page?: number;
    skip?: number; // deprecated
    limit?: number; // deprecated
    event_ids?: string[];
  },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<QuestionRead, RankedListMeta>>> => {
  return customInstance<PaginatedResponse<QuestionRead, RankedListMeta>>({
    url: '/api/v1/questions',
    method: 'GET',
    params,
    ...options,
  });
};

// AIDEV-NOTE: Public questions endpoint for guest browsing - no auth required
export const readPublicQuestionsApiV1QuestionsPublicGet = (
  params?: {
    cursor?: number;
    page?: number;
    per_page?: number;
    event_ids?: string[];
  },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<QuestionRead, RankedListMeta>>> => {
  return customInstance<PaginatedResponse<QuestionRead, RankedListMeta>>({
    url: '/api/v1/questions/public',
    method: 'GET',
    params,
    ...options,
  });
};

export const readQuestionsTotalsApiV1QuestionsTotalsGet = (
  params?: { event_ids?: string[] },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<{ total: number }>> => {
  return customInstance<{ total: number }>({
    url: '/api/v1/questions/totals',
    method: 'GET',
    params,
    ...options,
  });
};

// AIDEV-NOTE: Meetverse endpoint now returns paginated response like main questions endpoint
export const readMeetverseQuestionsApiV1QuestionsMeetverseGet = (
  params?: {
    cursor?: number;
    page?: number;
    per_page?: number;
    skip?: number; // deprecated
    limit?: number; // deprecated
    sort_by?: string;
  },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<QuestionRead, RankedListMeta>>> => {
  return customInstance<PaginatedResponse<QuestionRead, RankedListMeta>>({
    url: '/api/v1/questions/meetverse',
    method: 'GET',
    params,
    ...options,
  });
};

export const getMySpacesFeedApiV1QuestionsMySpacesFeedGet = (
  params?: {
    cursor?: number;
    page?: number;
    per_page?: number;
    sort_by?: string;
  },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<QuestionRead, RankedListMeta>>> => {
  return customInstance<PaginatedResponse<QuestionRead, RankedListMeta>>({
    url: '/api/v1/questions/my-spaces-feed',
    method: 'GET',
    params,
    ...options,
  });
};

export const readQuestionApiV1QuestionsQuestionIdGet = (
  questionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<QuestionRead>> => {
  return customInstance<QuestionRead>({
    url: `/api/v1/questions/${questionId}`,
    method: 'GET',
    ...options,
  });
};

export const resolveQuestionBySlug = (
  slug: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<QuestionDetailRead>> => {
  return customInstance<QuestionDetailRead>({
    url: `/api/v1/resolve/questions/${encodeURIComponent(slug)}`,
    method: 'GET',
    ...options,
  });
};

export const readQuestionAllInteractionsApiV1QuestionsQuestionIdAllInteractionsGet =
  (
    questionId: string,
    params?: { skip?: number; limit?: number },
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<InteractionRead[]>> => {
    return customInstance<InteractionRead[]>({
      url: `/api/v1/questions/${questionId}/all_interactions`,
      method: 'GET',
      params,
      ...options,
    });
  };

export const readQuestionUpliftUsersApiV1QuestionsQuestionIdUpliftGet = (
  questionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserRead[]>> => {
  return customInstance<UserRead[]>({
    url: `/api/v1/questions/${questionId}/uplift`,
    method: 'GET',
    ...options,
  });
};

export const createQuestionApiV1QuestionsPost = (
  data: QuestionCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<QuestionRead>> => {
  return customInstance<QuestionRead>({
    url: '/api/v1/questions',
    method: 'POST',
    data,
    ...options,
  });
};

/**
 * Share an existing question into more spaces (asker only, async-matching #22).
 * Server semantics: auto-joins the asker into frictionless public targets
 * (real APPROVED membership), returns `skipped_gated` for gated targets,
 * `already_there` for idempotent re-shares; one bad target never fails the
 * batch. Over the MAX_QUESTION_SPACES cap -> 422 with
 * `detail.offending_space_ids` (reject-not-clamp).
 */
export const shareQuestionToSpaces = (
  questionId: string,
  spaceIds: readonly string[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ShareQuestionToSpacesResponse>> => {
  return customInstance<ShareQuestionToSpacesResponse>({
    url: `/api/v1/questions/${encodeURIComponent(questionId)}/share`,
    method: 'POST',
    data: { space_ids: [...spaceIds] } satisfies ShareQuestionToSpacesRequest,
    ...options,
  });
};

// =============================================================================
// PENDING QUESTIONS API
// AIDEV-NOTE: Used for guest question submission and claiming after signup
// =============================================================================

/**
 * Create a pending question (no auth required - for guest users)
 */
export const createPendingQuestionApiV1QuestionsPost = (
  data: PendingQuestionCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PendingQuestionRead>> => {
  return customInstance<PendingQuestionRead>({
    url: '/api/v1/questions/pending',
    method: 'POST',
    data,
    ...options,
  });
};

/**
 * Request a magic sign-in link (no auth required). Always 202 regardless of account existence.
 *
 * `flow_context` is OPTIONAL and omitted unless the visitor arrived at the door
 * with something pending. When present the server stashes it under an opaque
 * `flow` id and appends that id to the link, which is the only channel that
 * survives the link opening in a different browsing context (installed PWA ->
 * Safari, an in-app mail browser, another device) where the localStorage mirrors
 * do not exist. See `MagicLinkFlowContext`.
 *
 * The 202 body is `MagicLinkAccepted`, not the bare `{}` this used to type:
 * with magic-link continuation on, the same response also carries
 * `flow_id`/`request_secret` for the requesting device to poll. Both are
 * optional so this keeps compiling and working against a pre-continuation
 * backend that still answers `{}`.
 */
export const requestMagicLinkApiV1AuthMagicLinkPost = (
  data: MagicLinkRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<MagicLinkAccepted>> => {
  return customInstance<MagicLinkAccepted>({
    url: '/api/v1/auth/magic-link',
    method: 'POST',
    data,
    ...options,
  });
};

// NOT HERE, deliberately: the flow-context READ (POST /api/v1/auth/magic-link/flow).
//
// A `//` block rather than a `/** */` one because a JSDoc comment attaches to the
// next declaration, and this one is about a function that is NOT below it.
//
// AIDEV-NOTE [MEETBALL magic-link]: it used to be a `customInstance` wrapper like
// everything else in this file, and this instance is the wrong transport for it.
// The request interceptor above `await`s `globalGetAuthToken()` -- which can block
// on a cross-tab token refresh, spending the landing screen's 4s budget before the
// POST is even on the wire, and attaches a bearer to an endpoint that is
// unauthenticated by design -- and the response interceptor forwards 5xx and
// network failures to `maybeProbe`, so one bad answer to a best-effort read can
// raise the global MaintenanceTakeover over a live magic-link redemption.
// `useMagicLinkFlowContext` therefore owns a bare `fetch` with no interceptors,
// `credentials: 'omit'` and its own clock. See that hook's docblock.

// NOT HERE: the pending-question claim (POST /api/v1/questions/pending/claim). It is
// sent by `claimPendingQuestions` in `@hooks/useClaimPendingQuestions`, with the bearer
// its caller hands it and none of this instance's interceptors. Its body and response
// are `PendingQuestionClaimRequest` and `PendingQuestionClaimResponse` in `@models/api`,
// and the limbs the backend matches on are documented at that function.

export const updateQuestionApiV1QuestionsQuestionIdPut = (
  questionId: string,
  data: QuestionUpdate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<QuestionRead>> => {
  return customInstance<QuestionRead>({
    url: `/api/v1/questions/${questionId}`,
    method: 'PUT',
    data,
    ...options,
  });
};

// DELETE and PUT /answered answer with the backend's QuestionUpdate model: the post's editable
// fields as they are after the write, and NO id (unlike the edit above, which answers with the
// read model). A caller that needs the post's id already has it: it is the id it sent.
export const deleteQuestionApiV1QuestionsQuestionIdDelete = (
  questionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<QuestionUpdate>> => {
  return customInstance<QuestionUpdate>({
    url: `/api/v1/questions/${questionId}`,
    method: 'DELETE',
    ...options,
  });
};

export const markQuestionAsAnsweredApiV1QuestionsQuestionIdAnsweredPut = (
  questionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<QuestionUpdate>> => {
  return customInstance<QuestionUpdate>({
    url: `/api/v1/questions/${questionId}/answered`,
    method: 'PUT',
    ...options,
  });
};

export const createInteractionApiV1InteractionsPost = (
  data: InteractionCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<InteractionToggleResponse>> => {
  return customInstance<InteractionToggleResponse>({
    url: '/api/v1/interactions',
    method: 'POST',
    data,
    ...options,
  });
};

// ── We Met v2 — bidirectional connection endpoints (TECH-295) ───────────────

export const createWeMetConnectionV2 = (
  body: WeMetConnectionCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<WeMetConnectionRead>> => {
  return customInstance<WeMetConnectionRead>({
    url: '/api/v1/interactions/we-met/v2',
    method: 'POST',
    data: body,
    ...options,
  }).then(tapWeMetV2Row);
};

export const listWeMetConnectionsV2 = (
  params?: { status_filter?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<WeMetConnectionRead[]>> => {
  return customInstance<WeMetConnectionRead[]>({
    url: '/api/v1/interactions/we-met/v2',
    method: 'GET',
    params,
    ...options,
  }).then(tapWeMetV2Rows);
};

export const confirmWeMetConnectionV2 = (
  connectionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<WeMetConnectionRead>> => {
  return customInstance<WeMetConnectionRead>({
    url: `/api/v1/interactions/we-met/v2/${connectionId}/confirm`,
    method: 'POST',
    ...options,
  }).then(tapWeMetV2Row);
};

export const declineWeMetConnectionV2 = (
  connectionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<WeMetConnectionRead>> => {
  return customInstance<WeMetConnectionRead>({
    url: `/api/v1/interactions/we-met/v2/${connectionId}/decline`,
    method: 'POST',
    ...options,
  }).then(tapWeMetV2Row);
};

export const deleteWeMetConnectionV2 = (
  connectionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<{ detail: string }>> => {
  return customInstance<{ detail: string }>({
    url: `/api/v1/interactions/we-met/v2/${connectionId}`,
    method: 'DELETE',
    ...options,
  });
};

export const deleteInteractionApiV1InteractionsInteractionIdDelete = (
  interactionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/interactions/${interactionId}`,
    method: 'DELETE',
    ...options,
  });
};
export const getQuestionByID = (
  questionID: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<QuestionDetailRead>> => {
  return customInstance<QuestionDetailRead>({
    url: `/api/v1/questions/${questionID}`,
    method: 'GET',
    ...options,
  });
};

export const getQuestionInteractions = (
  questionID: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<QuestionInteractionsResponse>> => {
  return customInstance<QuestionInteractionsResponse>({
    url: `/api/v1/questions/${questionID}/interactions`,
    method: 'GET',
    ...options,
  });
};

export const getMeTooInteractionByQuestionId = (
  questionID: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserProfileResponse[]>> => {
  return customInstance<UserProfileResponse[]>({
    url: `/api/v1/questions/${questionID}/me_too`,
    method: 'GET',
    ...options,
  });
};

export const getIcanHelpInteractionByQuestionId = (
  questionID: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/questions/${questionID}/i_can_help`,
    method: 'GET',
    ...options,
  });
};

export const readInteractionsApiV1InteractionsGet = (
  params?: {
    page?: number;
    per_page?: number;
    user_id?: string;
    other_user_id?: string;
    interaction_type?: string;
    target_type?: string;
    filter_out?: string;
  },
  options?: AxiosRequestConfig,
  // AIDEV-TODO: Replace 'any' with union type InteractionRead[] | InteractionsResponse - see GitHub issue
  // AIDEV-NOTE: Both Copilot and colleague suggested proper typing instead of 'any'
): Promise<AxiosResponse<InteractionRead[] | InteractionsResponse>> => {
  return customInstance<InteractionRead[] | InteractionsResponse>({
    url: '/api/v1/interactions',
    method: 'GET',
    params,
    ...options,
  });
};

export const createEventParticipantApiV1EventParticipantsPost = (
  data: EventParticipantCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<EventParticipantRead>> => {
  return customInstance<EventParticipantRead>({
    url: '/api/v1/event-participants',
    method: 'POST',
    data,
    ...options,
  });
};

export const readAnswersApiV1AnswersGet = (
  params?: { skip?: number; limit?: number },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<{ data: AnswerRead[] }>> => {
  return customInstance<{ data: AnswerRead[] }>({
    url: '/api/v1/answers',
    method: 'GET',
    params,
    ...options,
  });
};

export const createAnswerApiV1AnswersPost = (
  data: AnswerCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<AnswerRead>> => {
  return customInstance<AnswerRead>({
    url: '/api/v1/answers',
    method: 'POST',
    data,
    ...options,
  });
};

export const readUsersApiV1UsersGet = (
  params?: { skip?: number; limit?: number; search?: string; exclude_current_user?: boolean },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserRead[]>> => {
  return customInstance<UserRead[]>({
    url: '/api/v1/users',
    method: 'GET',
    params,
    ...options,
  });
};

/**
 * Minimal user data for search/autocomplete - privacy-focused response
 * AIDEV-NOTE: Backend returns only these fields to protect user privacy in tagging contexts
 * No email, no role fields (has_extra_options, is_champion) - only display-safe data
 */
export interface UserSearchResult {
  id: string;
  full_name: string;
  profile_picture: string | null;
  hashtags: string | null;
  slug: string;
}

/**
 * Search users with privacy-focused minimal response
 * AIDEV-NOTE: Use this for user tagging/autocomplete (TagFriendDrawer, UserTagSelector)
 * Returns only: id, full_name, profile_picture, hashtags, slug
 * Does NOT return: email, auth_id, has_extra_options, is_champion, is_active
 */
export const searchUsersApiV1UsersSearchGet = (
  params?: { q: string; limit?: number },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserSearchResult[]>> => {
  return customInstance<UserSearchResult[]>({
    url: '/api/v1/users/search',
    method: 'GET',
    params,
    ...options,
  });
};

/**
 * Admin-only ICP / people search: a product/need description in, ICP archetypes
 * → real companies → Hunter-resolved decision-maker emails out. Admin-gated server-side.
 */
export const icpSearchApiV1AdminIcpSearchPost = (
  body: IcpSearchRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<IcpSearchResponse>> => {
  return customInstance<IcpSearchResponse>({
    url: '/api/v1/admin/icp-search',
    method: 'POST',
    data: body,
    ...options,
  });
};

// AIDEV-NOTE: Added filter param for "ask on behalf" feature
export type UserQuestionsFilter = 'created_by_me' | 'asked_for_me' | null;

export const readUserQuestionsApiV1UsersUserIdQuestionsGet = (
  userId: string,
  params?: { skip?: number; limit?: number; filter?: UserQuestionsFilter },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<QuestionProfileRead[]>> => {
  return customInstance<QuestionProfileRead[]>({
    url: `/api/v1/users/${userId}/questions`,
    method: 'GET',
    params,
    ...options,
  });
};


// React Query hooks
export const useReadEventsApiV1EventsGet = (
  params?: {
    page?: number;
    per_page?: number;
    user_id?: string;
    pov?: EventsPov;
    search?: string;
  },
  options?: UseQueryOptions<AxiosResponse<EventRead[]>, Error>,
) => {
  return useQuery({
    queryKey: eventsListKey(params),
    queryFn: () => readEventsApiV1EventsGet(params),
    ...options,
  });
};

export const useReadEventsTotalsApiV1EventsTotalsGet = (
  params?: { pov?: EventsPov; user_id?: string },
  options?: UseQueryOptions<AxiosResponse<{ upcoming: number; live: number; past: number; total: number }>, Error>,
) => {
  return useQuery({
    queryKey: eventsTotalsKey(params),
    queryFn: () => readEventsTotalsApiV1EventsTotalsGet(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

export const useReadQuestionsTotalsApiV1QuestionsTotalsGet = (
  params?: { event_ids?: string[] },
  options?: UseQueryOptions<AxiosResponse<{ total: number }>, Error>,
) => {
  return useQuery({
    queryKey: questionTotalsKey(params),
    queryFn: () => readQuestionsTotalsApiV1QuestionsTotalsGet(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

export const useReadQuestionsApiV1QuestionsGet = (
  // AIDEV-TODO: change to `page` and `per_page` params
  params?: { skip?: number; limit?: number; event_id?: string },
  options?: UseQueryOptions<AxiosResponse<{ data: QuestionRead[] }>, Error>,
) => {
  return useQuery({
    queryKey: questionsParamsKey(params),
    queryFn: () => readQuestionsApiV1QuestionsGet(params),
    ...options,
  });
};

export const useReadMeetverseQuestionsApiV1QuestionsMeetverseGet = (
  options?: Omit<
    UseQueryOptions<AxiosResponse<PaginatedResponse<QuestionRead, RankedListMeta>>, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: questionsMeetverseKey(),
    queryFn: () => readMeetverseQuestionsApiV1QuestionsMeetverseGet(),
    ...options,
  });
};

export const useReadQuestionApiV1QuestionsQuestionIdGet = (
  questionId: string,
  options?: UseQueryOptions<AxiosResponse<QuestionRead>, Error>,
) => {
  return useQuery({
    queryKey: questionDetailKey(questionId),
    queryFn: () => readQuestionApiV1QuestionsQuestionIdGet(questionId),
    enabled: !!questionId,
    ...options,
  });
};

export const useReadQuestionUpliftUsersApiV1QuestionsQuestionIdUpliftGet = (
  questionId: string,
  options?: UseQueryOptions<AxiosResponse<UserRead[]>, Error>,
) => {
  return useQuery({
    queryKey: questionUpliftUsersKey(questionId),
    queryFn: () =>
      readQuestionUpliftUsersApiV1QuestionsQuestionIdUpliftGet(questionId),
    enabled: !!questionId,
    ...options,
  });
};

export const useReadInteractionsApiV1InteractionsGet = (
  params?: {
    page?: number;
    per_page?: number;
    user_id?: string;
    other_user_id?: string;
    interaction_type?: string;
    target_type?: string;
    filter_out?: string;
  },
  options?: Omit<
    UseQueryOptions<
      AxiosResponse<InteractionRead[] | InteractionsResponse>,
      Error
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: interactionsKey(params),
    queryFn: () => readInteractionsApiV1InteractionsGet(params),
    // AIDEV-NOTE: Enable when both IDs provided (filtered) OR when no user_id (unfiltered)
    // AIDEV-NOTE: Copilot suggested incorrect logic that would break unfiltered queries
    enabled: !!(params?.user_id && params?.other_user_id) || !params?.user_id,
    ...options,
  });
};

export const useCreateQuestionApiV1QuestionsPost = (
  options?: UseMutationOptions<
    AxiosResponse<QuestionRead>,
    Error,
    { data: QuestionCreate }
  >,
) => {
  return useMutation({
    mutationFn: ({ data }) => createQuestionApiV1QuestionsPost(data),
    ...options,
  });
};

export const useUpdateQuestionApiV1QuestionsQuestionIdPut = (
  options?: UseMutationOptions<
    AxiosResponse<QuestionRead>,
    Error,
    { questionId: string; data: QuestionUpdate }
  >,
) => {
  return useMutation({
    mutationFn: ({ questionId, data }) =>
      updateQuestionApiV1QuestionsQuestionIdPut(questionId, data),
    ...options,
  });
};

export const useDeleteQuestionApiV1QuestionsQuestionIdDelete = (
  options?: UseMutationOptions<
    AxiosResponse<QuestionUpdate>,
    Error,
    { questionId: string }
  >,
) => {
  return useMutation({
    mutationFn: ({ questionId }) =>
      deleteQuestionApiV1QuestionsQuestionIdDelete(questionId),
    ...options,
  });
};

// AIDEV-NOTE: "Ask on behalf" feature - claim question API hook
export const claimQuestionApiV1QuestionsQuestionIdClaimPut = async (
  questionId: string,
): Promise<AxiosResponse<QuestionRead>> => {
  return customInstance<QuestionRead>({
    url: `/api/v1/questions/${questionId}/claim`,
    method: 'PUT',
  });
};

export const useClaimQuestionApiV1QuestionsQuestionIdClaimPut = (
  options?: UseMutationOptions<
    AxiosResponse<QuestionRead>,
    Error,
    { questionId: string }
  >,
) => {
  return useMutation({
    mutationFn: ({ questionId }) =>
      claimQuestionApiV1QuestionsQuestionIdClaimPut(questionId),
    ...options,
  });
};

// AIDEV-NOTE: "Ask on behalf" feature - decline question API hook
export const declineQuestionApiV1QuestionsQuestionIdDeclinePut = async (
  questionId: string,
): Promise<AxiosResponse<QuestionRead>> => {
  return customInstance<QuestionRead>({
    url: `/api/v1/questions/${questionId}/decline`,
    method: 'PUT',
  });
};

export const useDeclineQuestionApiV1QuestionsQuestionIdDeclinePut = (
  options?: UseMutationOptions<
    AxiosResponse<QuestionRead>,
    Error,
    { questionId: string }
  >,
) => {
  return useMutation({
    mutationFn: ({ questionId }) =>
      declineQuestionApiV1QuestionsQuestionIdDeclinePut(questionId),
    ...options,
  });
};

export const useMarkQuestionAsAnsweredApiV1QuestionsQuestionIdAnsweredPut = (
  options?: UseMutationOptions<
    AxiosResponse<QuestionUpdate>,
    Error,
    { questionId: string }
  >,
) => {
  return useMutation({
    mutationFn: ({ questionId }) =>
      markQuestionAsAnsweredApiV1QuestionsQuestionIdAnsweredPut(questionId),
    ...options,
  });
};

export const useCreateInteractionApiV1InteractionsPost = (
  options?: UseMutationOptions<
    AxiosResponse<InteractionToggleResponse>,
    Error,
    { data: InteractionCreate }
  >,
) => {
  return useMutation({
    mutationFn: ({ data }) => createInteractionApiV1InteractionsPost(data),
    ...options,
  });
};

export const useDeleteInteractionApiV1InteractionsInteractionIdDelete = (
  options?: UseMutationOptions<
    AxiosResponse<void>,
    Error,
    { interactionId: string }
  >,
) => {
  return useMutation({
    mutationFn: ({ interactionId }) =>
      deleteInteractionApiV1InteractionsInteractionIdDelete(interactionId),
    ...options,
  });
};
export const useGetQuestionByID = (questionID: string | undefined) => {
  return useQuery({
    queryKey: questionDetailKey(questionID),
    queryFn: () => {
      if (!questionID) {
        throw new Error('Question ID is required');
      }
      return getQuestionByID(questionID);
    },
    enabled: !!questionID,
    // AIDEV-NOTE: Don't retry on 403 (private question access denied for guests)
    retry: skipRetryOn403,
  });
};

export const useGetQuestionInteractions = (
  questionID: string | undefined,
  options?: UseQueryOptions<AxiosResponse<QuestionInteractionsResponse>, Error>,
) => {
  return useQuery({
    queryKey: questionInteractionsKey(questionID),
    queryFn: () => {
      if (!questionID) {
        throw new Error('Question ID is required');
      }
      return getQuestionInteractions(questionID);
    },
    enabled: !!questionID,
    ...options,
  });
};
export const useGetMeTootInteractionByQuestionId = (
  questionID: string | undefined,
  options?: UseQueryOptions<AxiosResponse<UserProfileResponse[]>, Error>,
) => {
  return useQuery({
    queryKey: meTooInteractionKey(questionID ?? ''),
    queryFn: () => {
      if (!questionID) {
        throw new Error('Question ID is required');
      }
      return getMeTooInteractionByQuestionId(questionID);
    },
    enabled: !!questionID,
    ...options,
  });
};
export const useGetICanHelpInteractionByQuestionId = (
  questionID: string | undefined,
  options?: UseQueryOptions<AxiosResponse<void>, Error>,
) => {
  return useQuery({
    queryKey: iCanHelpInteractionKey(questionID),
    queryFn: () => {
      if (!questionID) {
        throw new Error('Question ID is required');
      }
      return getIcanHelpInteractionByQuestionId(questionID);
    },
    enabled: !!questionID,
    ...options,
  });
};
export const useCreateEventParticipantApiV1EventParticipantsPost = (
  options?: UseMutationOptions<
    AxiosResponse<EventParticipantRead>,
    Error,
    { data: EventParticipantCreate }
  >,
) => {
  return useMutation({
    mutationFn: ({ data }) =>
      createEventParticipantApiV1EventParticipantsPost(data),
    ...options,
  });
};

export const useCreateEventApiV1EventsPost = (
  options?: UseMutationOptions<
    AxiosResponse<EventRead>,
    Error,
    { data: EventCreate }
  >,
) => {
  return useMutation({
    mutationFn: ({ data }) => createEventApiV1EventsPost(data),
    ...options,
  });
};

export const useImportEventApiV1EventsImportPost = (
  options?: UseMutationOptions<
    AxiosResponse<EventImportResponse>,
    Error,
    { data: EventImportRequest }
  >,
) => {
  return useMutation({
    mutationFn: ({ data }) => importEventApiV1EventsImportPost(data),
    ...options,
  });
};

export const useUpdateEventApiV1EventsEventIdPut = (
  options?: UseMutationOptions<
    AxiosResponse<EventRead>,
    Error,
    { eventId: string; data: EventUpdate }
  >,
) => {
  return useMutation({
    mutationFn: ({ eventId, data }) =>
      updateEventApiV1EventsEventIdPut(eventId, data),
    ...options,
  });
};

export const useDeleteEventApiV1EventsEventIdDelete = (
  options?: UseMutationOptions<
    AxiosResponse<EventUpdate>,
    Error,
    { eventId: string }
  >,
) => {
  return useMutation({
    mutationFn: ({ eventId }) => deleteEventApiV1EventsEventIdDelete(eventId),
    ...options,
  });
};

export const useReadFeaturedEventsApiV1EventsFeaturedGet = (
  params?: { skip?: number; limit?: number },
  options?: UseQueryOptions<AxiosResponse<EventRead[]>, Error>,
) => {
  return useQuery({
    queryKey: featuredEventsListKey(params),
    queryFn: () => readFeaturedEventsApiV1EventsFeaturedGet(params),
    ...options,
  });
};

export const useUpdateEventFeaturedStatusApiV1EventsEventIdFeaturedPatch = (
  options?: UseMutationOptions<
    AxiosResponse<EventRead>,
    Error,
    { eventId: string; is_featured: boolean }
  >,
) => {
  return useMutation({
    mutationFn: ({ eventId, is_featured }) =>
      updateEventFeaturedStatusApiV1EventsEventIdFeaturedPatch(eventId, is_featured),
    ...options,
  });
};

export const useClaimEventApiV1EventsEventIdClaimPost = (
  options?: UseMutationOptions<
    AxiosResponse<void>,
    Error,
    { eventId: string }
  >,
) => {
  return useMutation({
    mutationFn: ({ eventId }) => claimEventApiV1EventsEventIdClaimPost(eventId),
    ...options,
  });
};

export const useReadFeaturedSpaces = (
  params?: { skip?: number; limit?: number },
  options?: Omit<
    UseQueryOptions<AxiosResponse<CommunityRead[]>, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: featuredSpacesListKey(params),
    queryFn: () => readFeaturedSpaces(params),
    ...options,
  });
};

export const useUpdateSpaceFeaturedStatus = (
  options?: UseMutationOptions<
    AxiosResponse<CommunityRead>,
    Error,
    { identifier: string; is_featured: boolean }
  >,
) => {
  return useMutation({
    mutationFn: ({ identifier, is_featured }) => updateSpaceFeaturedStatus(identifier, is_featured),
    ...options,
  });
};

export const useSetEventOwnerApiV1EventsEventIdSetOwnerPost = (
  options?: UseMutationOptions<
    AxiosResponse<EventRead>,
    Error,
    { eventId: string; data: { user_id: string } }
  >,
) => {
  return useMutation({
    mutationFn: ({ eventId, data }) =>
      setEventOwnerApiV1EventsEventIdSetOwnerPost(eventId, data),
    ...options,
  });
};

export const useReadEventApiV1EventsEventIdGet = (
  eventId: string,
  options?: Omit<
    UseQueryOptions<AxiosResponse<EventRead>, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: eventKey(eventId),
    queryFn: () => readEventApiV1EventsEventIdGet(eventId),
    enabled: !!eventId,
    ...options,
  });
};

export const useGetUserParticipationStatusApiV1EventsEventIdParticipationGet = (
  eventId: string,
  userId: string,
  options?: Omit<
    UseQueryOptions<AxiosResponse<UserParticipationStatus>, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: eventParticipationKey(eventId, userId),
    queryFn: () =>
      getUserParticipationStatusApiV1EventsEventIdParticipationGet(
        eventId,
        userId,
      ),
    enabled: !!eventId && !!userId,
    ...options,
  });
};

export const useCheckinToEventApiV1EventsEventIdCheckinPost = (
  options?: UseMutationOptions<
    AxiosResponse<CheckInResponse>,
    Error,
    { eventId: string; userId: string; data: CheckInRequest }
  >,
) => {
  return useMutation({
    mutationFn: ({ eventId, userId, data }) =>
      checkinToEventApiV1EventsEventIdCheckinPost(eventId, userId, data),
    ...options,
  });
};

export const useGetMyCheckedInEventsApiV1EventsMyCheckedInGet = (
  includeMeetverse?: boolean,
  options?: Omit<
    UseQueryOptions<AxiosResponse<EventRead[]>, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: myCheckedInEventsKey(includeMeetverse),
    queryFn: () =>
      getMyCheckedInEventsApiV1EventsMyCheckedInGet(includeMeetverse),
    enabled: true,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes - this data doesn't change often
    retry: 3, // Limit to 3 retry attempts
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    ...options,
  });
};

export const useCreateAnswerApiV1AnswersPost = (
  options?: UseMutationOptions<
    AxiosResponse<AnswerRead>,
    Error,
    { data: AnswerCreate }
  >,
) => {
  return useMutation({
    mutationFn: ({ data }) => createAnswerApiV1AnswersPost(data),
    ...options,
  });
};

export const useReadAnswersApiV1AnswersGet = (
  params?: { skip?: number; limit?: number },
  options?: UseQueryOptions<AxiosResponse<{ data: AnswerRead[] }>, Error>,
) => {
  return useQuery({
    queryKey: answersListKey(params),
    queryFn: () => readAnswersApiV1AnswersGet(params),
    ...options,
  });
};

export const useReadUsersApiV1UsersGet = (
  params?: { skip?: number; limit?: number },
  options?: UseQueryOptions<AxiosResponse<UserRead[]>, Error>,
) => {
  return useQuery({
    queryKey: usersListKey(params),
    queryFn: () => readUsersApiV1UsersGet(params),
    ...options,
  });
};

export const useReadUserQuestionsApiV1UsersUserIdQuestionsGet = (
  userId: string,
  params?: { skip?: number; limit?: number; filter?: UserQuestionsFilter },
  options?: UseQueryOptions<AxiosResponse<QuestionProfileRead[]>, Error>,
) => {
  return useQuery({
    queryKey: userQuestionsKey(userId, params),
    queryFn: () =>
      readUserQuestionsApiV1UsersUserIdQuestionsGet(userId, params),
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    enabled: !!userId, // Only run query if userId is provided
    ...options,
  });
};

// =============================================================================
// NOTIFICATIONS API
// =============================================================================

/**
 * Get paginated notifications for authenticated user
 */
export const getNotificationsApiV1NotificationsGet = (
  params?: { page?: number; per_page?: number },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<NotificationListResponse>> => {
  return customInstance<NotificationListResponse>({
    url: '/api/v1/notifications',
    method: 'GET',
    params: params,
    ...options,
  });
};

/**
 * Mark all notifications as read for authenticated user
 */
export const markAllNotificationsReadApiV1NotificationsPost = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<{ message: string; updated_count: number }>> => {
  return customInstance<{ message: string; updated_count: number }>({
    url: '/api/v1/notifications?mark_all_read=true',
    method: 'POST',
    ...options,
  });
};

/**
 * Mark specific notification with a status (read or deleted)
 */
export const markNotificationApiV1NotificationsNotificationIdPost = (
  notificationId: string,
  markAs: 'read' | 'deleted',
  options?: AxiosRequestConfig,
): Promise<
  AxiosResponse<{ message: string; notification_id: string; status: string }>
> => {
  return customInstance<{
    message: string;
    notification_id: string;
    status: string;
  }>({
    url: `/api/v1/notifications/${notificationId}?mark_as=${markAs}`,
    method: 'POST',
    ...options,
  });
};

/**
 * Unread notification count for the authenticated user (N3 "honest bell").
 * Server counts ONLY status='unread' - no page scan, no client-side filter.
 */
export const getNotificationUnreadCount = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<NotificationUnreadCountResponse>> => {
  return customInstance<NotificationUnreadCountResponse>({
    url: '/api/v1/notifications/unread-count',
    method: 'GET',
    ...options,
  });
};

/**
 * Record that the recipient opened/actioned a notification (N3 "honest bell").
 * The backend stamps clicked_at (first click wins) and advances an unread row
 * to read, returning 204 No Content. A wrong/foreign/non-visible id 404s - the
 * caller treats that (and an expired session) as a silent no-op.
 */
export const clickNotificationApiV1NotificationsNotificationIdClickPost = (
  notificationId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/notifications/${encodeURIComponent(notificationId)}/click`,
    method: 'POST',
    ...options,
  });
};

// React Query Hooks for Notifications

/**
 * Hook to mark all notifications as read
 */
export const useMarkAllNotificationsReadApiV1NotificationsPost = (
  options?: UseMutationOptions<
    AxiosResponse<{ message: string; updated_count: number }>,
    Error
  >,
) => {
  return useMutation({
    mutationFn: () => markAllNotificationsReadApiV1NotificationsPost(),
    ...options,
  });
};

/**
 * Hook to mark notification with status (read or deleted)
 */
export const useMarkNotificationApiV1NotificationsNotificationIdPost = (
  options?: UseMutationOptions<
    AxiosResponse<{ message: string; notification_id: string; status: string }>,
    Error,
    { notificationId: string; markAs: 'read' | 'deleted' }
  >,
) => {
  return useMutation({
    mutationFn: ({ notificationId, markAs }) =>
      markNotificationApiV1NotificationsNotificationIdPost(
        notificationId,
        markAs,
      ),
    ...options,
  });
};

/**
 * Validate a referral code
 */
export const validateReferralCode = async (referralCode: string): Promise<{
  valid: boolean;
  referrer: {
    id: string;
    full_name: string;
    is_champion: boolean;
  };
  message: string;
}> => {
  try {
    const response = await customInstance<{
      valid: boolean;
      referrer: {
        id: string;
        full_name: string;
        is_champion: boolean;
      };
      message: string;
    }>({
      method: 'POST',
      url: `/api/v1/users/validate-referral-code?referral_code=${encodeURIComponent(referralCode)}`,
    });

    return response.data;
  } catch (error) {
    logger.error('Error validating referral code:', error as Error);
    throw error;
  }
};

/**
 * Admin API: Promote user to admin by email
 */
export const promoteToAdminByEmail = (
  data: { email: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<{ message: string; user_id: string; email: string }>> => {
  return customInstance({
    url: '/api/v1/admin/promote-by-email',
    method: 'POST',
    data,
    ...options,
  });
};

export const usePromoteToAdminByEmail = (
  options?: UseMutationOptions<
    AxiosResponse<{ message: string; user_id: string; email: string }>,
    Error,
    { data: { email: string } }
  >,
) => {
  return useMutation({
    mutationFn: ({ data }) => promoteToAdminByEmail(data),
    ...options,
  });
};

/**
 * Admin API: Make user champion by email
 */
export const makeChampionByEmail = (
  data: { email: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<{ success: boolean; message: string; champion_referral_code: string; user_id: string; email: string }>> => {
  return customInstance({
    url: '/api/v1/admin/make-champion-by-email',
    method: 'POST',
    data,
    ...options,
  });
};

export const useMakeChampionByEmail = (
  options?: UseMutationOptions<
    AxiosResponse<{ success: boolean; message: string; champion_referral_code: string; user_id: string; email: string }>,
    Error,
    { data: { email: string } }
  >,
) => {
  return useMutation({
    mutationFn: ({ data }) => makeChampionByEmail(data),
    ...options,
  });
};

// =============================================================================
// NATIVE PUSH NOTIFICATION DEVICE REGISTRATION
// =============================================================================

export type {
  DeviceRegistrationRequest,
  DeviceRegistrationResponse,
} from '@models/api/deviceInstallation';
import type {
  DeviceRegistrationRequest,
  DeviceRegistrationResponse,
} from '@models/api/deviceInstallation';

/**
 * Register a native device for push notifications (FCM token).
 * The user ID is extracted from the JWT on the backend.
 */
export const registerNativeDevice = (
  data: DeviceRegistrationRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<DeviceRegistrationResponse>> => {
  return customInstance<DeviceRegistrationResponse>({
    url: '/api/v1/push/register-device',
    method: 'POST',
    data,
    ...options,
  });
};

/**
 * Unregister a native device from push notifications.
 * Deactivates the device token so no further pushes are sent.
 */
export const unregisterNativeDevice = (
  data: { push_token: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<{ message: string }>> => {
  return customInstance<{ message: string }>({
    url: '/api/v1/push/unregister-device',
    method: 'DELETE',
    data,
    ...options,
  });
};

// =============================================================================
// WEB PUSH NOTIFICATIONS
// =============================================================================

/**
 * Get the backend's push migration status (whether OneSignal delivery is live,
 * i.e. ONESIGNAL_PUSH_ENABLED is on). Unauthenticated, like the VAPID key.
 * The OneSignal owner path gates legacy teardown on this so a device never
 * deletes its legacy subscription before the replacement sender is active.
 */
export const getPushMigrationStatus = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PushMigrationStatusResponse>> => {
  return customInstance<PushMigrationStatusResponse>({
    url: '/api/v1/push/migration-status',
    method: 'GET',
    ...options,
  });
};

/**
 * Get the VAPID public key for Web Push API subscription.
 */
export const getVapidPublicKey = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<VAPIDPublicKeyResponse>> => {
  return customInstance<VAPIDPublicKeyResponse>({
    url: '/api/v1/push/vapid-key',
    method: 'GET',
    ...options,
  });
};

/**
 * Subscribe a browser to web push notifications.
 * Backend upserts by endpoint URL (idempotent).
 */
export const subscribeToPush = (
  data: PushSubscriptionCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PushSubscriptionRead>> => {
  return customInstance<PushSubscriptionRead>({
    url: '/api/v1/push/subscribe',
    method: 'POST',
    data,
    ...options,
  });
};

/**
 * Unsubscribe a browser from web push notifications.
 *
 * Sends `endpoint` as a query parameter so it actually reaches the backend.
 * The FastAPI handler at `app/api/api_v1/endpoints/push.py:92` declares
 * `subscription_id` and `endpoint` after `*,` with no `Body(...)` annotation,
 * which makes them query params. Sending them in the request body silently
 * fails: FastAPI sees `endpoint=None` and returns 400, leaving an orphan row
 * in `push_subscriptions` while the browser-side `subscription.unsubscribe()`
 * still succeeds.
 *
 * Backend returns 204 No Content on success; the response is typed as `void`.
 */
export const unsubscribeFromPush = (
  params: { endpoint: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: '/api/v1/push/unsubscribe',
    method: 'DELETE',
    ...options,
    // `params` is spread last so caller-supplied `options.params` cannot
    // silently clobber `endpoint` and recreate the 400/orphan-subscription
    // bug this helper exists to lock down.
    params,
  });
};

/**
 * Get all push subscriptions for the authenticated user.
 */
export const getMyPushSubscriptions = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PushSubscriptionRead[]>> => {
  return customInstance<PushSubscriptionRead[]>({
    url: '/api/v1/push/subscriptions',
    method: 'GET',
    ...options,
  });
};

// =============================================================================
// ACCOUNT DELETION
// =============================================================================

/**
 * Delete (deactivate) a user account with a grace period.
 * The user can reactivate by signing back in within the grace period.
 *
 * @param userId - The user's ID (must be the authenticated user's own ID, unless admin)
 * @param data - Must contain `{ confirmation: "DELETE" }` (exact string match required by backend)
 * @param options - Optional Axios request config
 */
export const deleteUserAccount = (
  userId: string,
  data: { confirmation: 'DELETE' },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserDeleteResponse>> => {
  return customInstance<UserDeleteResponse>({
    url: `/api/v1/users/${encodeURIComponent(userId)}`,
    method: 'DELETE',
    data,
    ...options,
  });
};

// Block user API functions
export const blockUser = (
  data: UserBlockCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<UserBlockRead>> => {
  return customInstance<UserBlockRead>({
    url: '/api/v1/blocks',
    method: 'POST',
    data,
    ...options,
  });
};

export const unblockUser = (
  blockedUserId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/blocks/${encodeURIComponent(blockedUserId)}`,
    method: 'DELETE',
    ...options,
  });
};

export const getBlockedUsers = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BlockedUserResponse[]>> => {
  return customInstance<BlockedUserResponse[]>({
    url: '/api/v1/blocks',
    method: 'GET',
    ...options,
  });
};

export const checkBlockStatus = (
  userId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BlockCheckResponse>> => {
  return customInstance<BlockCheckResponse>({
    url: `/api/v1/blocks/check/${encodeURIComponent(userId)}`,
    method: 'GET',
    ...options,
  });
};

export const createReport = (
  data: ContentReportCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ContentReportRead>> => {
  return customInstance<ContentReportRead>({
    url: '/api/v1/reports',
    method: 'POST',
    data,
    ...options,
  });
};

/**
 * Admin-only: list content reports, optionally filtered by moderation status.
 * Omitting `status` returns every status. Returns the unwrapped envelope body.
 */
export const getAdminReports = async (
  params: { status?: ReportStatus; page?: number; per_page?: number } = {},
  options?: AxiosRequestConfig,
): Promise<AdminReportsResponse> => {
  const response = await customInstance<AdminReportsResponse>({
    url: '/api/v1/admin/reports',
    method: 'GET',
    params,
    ...options,
  });
  return response.data;
};

/**
 * Admin-only: resolve or dismiss a single content report. Returns the updated
 * report (unwrapped). 404s when `reportId` is unknown.
 */
export const updateAdminReportStatus = async (
  reportId: string,
  body: ContentReportStatusUpdate,
  options?: AxiosRequestConfig,
): Promise<ContentReportAdminRead> => {
  const response = await customInstance<ContentReportAdminRead>({
    url: `/api/v1/admin/reports/${encodeURIComponent(reportId)}`,
    method: 'PATCH',
    data: body,
    ...options,
  });
  return response.data;
};

// ─── Spaces / Communities ──────────────────────────────────────────

export const listSpaceCategories = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceCategory[]>> => {
  return customInstance<SpaceCategory[]>({
    url: '/api/v1/spaces/categories',
    method: 'GET',
    ...options,
  });
};

export const listCommunities = (
  params?: { page?: number; per_page?: number; search?: string; cursor?: number },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<CommunityRead, RankedListMeta>>> => {
  return customInstance<PaginatedResponse<CommunityRead, RankedListMeta>>({
    url: '/api/v1/spaces',
    method: 'GET',
    params: { ...params, type: 'community' },
    ...options,
  });
};

/**
 * List featured community spaces for the discovery "Featured" rail.
 * Public endpoint (mirrors `readFeaturedEventsApiV1EventsFeaturedGet`).
 * Backend orders by the same weighted discovery ranking as the main list.
 */
export const readFeaturedSpaces = (
  params?: { skip?: number; limit?: number },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<CommunityRead[]>> => {
  return customInstance<CommunityRead[]>({
    url: '/api/v1/spaces/featured',
    method: 'GET',
    params,
    ...options,
  });
};

/**
 * Toggle a space's featured status (Meetball platform admin only).
 * Mirrors `updateEventFeaturedStatusApiV1EventsEventIdFeaturedPatch`:
 * `is_featured` rides as a query param, not a body.
 */
export const updateSpaceFeaturedStatus = (
  identifier: string,
  is_featured: boolean,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<CommunityRead>> => {
  return customInstance<CommunityRead>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/featured`,
    method: 'PATCH',
    params: { is_featured },
    ...options,
  });
};

export const listMyJoinedSpaces = (
  params?: {
    type?: string;
    role?: 'admin' | 'moderator' | 'member';
    page?: number;
    per_page?: number;
    /** Held posts (2026-10): also return PENDING memberships, each with its real
     * `user_membership_status`. Omitted = approved only (the backend default). */
    include_pending?: boolean;
  },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<CommunityRead>>> => {
  return customInstance<PaginatedResponse<CommunityRead>>({
    url: '/api/v1/spaces/my-joined',
    method: 'GET',
    params,
    ...options,
  });
};

/**
 * List active spaces where the target user is an approved member.
 *
 * Backend GET /api/v1/users/{user_id}/spaces (from backend PR #285 / issue #276).
 * Backend enforces viewer-privacy rules server-side:
 *   - Unlisted spaces hidden unless the viewer is also an approved member.
 *   - Space-level BLOCKED membership for the viewer excludes the row.
 *   - User-level block between viewer and space creator (either direction)
 *     excludes the row.
 *   - Only `SpaceStatus.active` spaces with target membership APPROVED.
 *
 * Returns the same `CommunityRead` shape as `listMyJoinedSpaces` so consumers
 * can share rendering logic.
 */
export const listUserSpaces = (
  userId: string,
  params?: { page?: number; per_page?: number },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<CommunityRead>>> => {
  return customInstance<PaginatedResponse<CommunityRead>>({
    url: `/api/v1/users/${encodeURIComponent(userId)}/spaces`,
    method: 'GET',
    params,
    ...options,
  });
};

export const getCommunityDetail = (
  identifier: string,
  params?: { token?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<CommunityRead>> => {
  return customInstance<CommunityRead>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}`,
    method: 'GET',
    params,
    ...options,
  });
};

export const createCommunity = (
  data: Omit<CommunityCreate, 'type'>,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<CommunityRead>> => {
  return customInstance<CommunityRead>({
    url: '/api/v1/spaces',
    method: 'POST',
    data: { ...data, type: 'community' },
    ...options,
  });
};

export const updateCommunity = (
  identifier: string,
  data: CommunityUpdate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<CommunityRead>> => {
  return customInstance<CommunityRead>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}`,
    method: 'PUT',
    data,
    ...options,
  });
};

export const joinCommunity = (
  spaceId: string,
  body?: { access_code?: string; invite_token?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceMemberRead>> => {
  return customInstance<SpaceMemberRead>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/members`,
    method: 'POST',
    data: body,
    ...options,
  });
};

export const leaveCommunity = (
  spaceId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/members`,
    method: 'DELETE',
    ...options,
  });
};

export interface ActivationProgressResponse {
  version: number;
  completed: Record<string, { at: string; refs?: Record<string, { at: string }> }>;
}

export const completeActivationSubStep = (
  spaceId: string,
  subStepId: string,
  ref?: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<ActivationProgressResponse>> =>
  customInstance<ActivationProgressResponse>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/activation/${encodeURIComponent(subStepId)}`,
    method: 'POST',
    ...(ref ? { data: { ref } } : {}),
    ...options,
  });

export const regenerateInviteToken = (
  identifier: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<CommunityRead>> => {
  return customInstance<CommunityRead>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/regenerate-token`,
    method: 'POST',
    ...options,
  });
};

export const regeneratePin = (
  identifier: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<CommunityRead>> => {
  return customInstance<CommunityRead>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/regenerate-pin`,
    method: 'POST',
    ...options,
  });
};

// ─── Space External Calendars ───────────────────────────────────────

export const listSpaceExternalCalendars = (
  identifier: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceExternalCalendarRead[]>> => {
  return customInstance<SpaceExternalCalendarRead[]>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/external-calendars`,
    method: 'GET',
    ...options,
  });
};

export const createSpaceExternalCalendar = (
  identifier: string,
  data: SpaceExternalCalendarCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceExternalCalendarRead>> => {
  return customInstance<SpaceExternalCalendarRead>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/external-calendars`,
    method: 'POST',
    data,
    ...options,
  });
};

export const updateSpaceExternalCalendar = (
  identifier: string,
  calendarId: string,
  data: SpaceExternalCalendarUpdate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceExternalCalendarRead>> => {
  return customInstance<SpaceExternalCalendarRead>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/external-calendars/${encodeURIComponent(calendarId)}`,
    method: 'PATCH',
    data,
    ...options,
  });
};

export const syncSpaceExternalCalendarNow = (
  identifier: string,
  calendarId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceExternalCalendarRead>> => {
  return customInstance<SpaceExternalCalendarRead>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/external-calendars/${encodeURIComponent(calendarId)}/sync`,
    method: 'POST',
    ...options,
  });
};

export const deleteSpaceExternalCalendar = (
  identifier: string,
  calendarId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/external-calendars/${encodeURIComponent(calendarId)}`,
    method: 'DELETE',
    ...options,
  });
};

/** The Space's pending duplicate pairs: calendar entries sync held back as likely copies of events. Admin only. */
export const listSpaceDuplicatePairs = (
  identifier: string,
  params: { page: number; per_page: number },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<DuplicatePair>>> => {
  return customInstance<PaginatedResponse<DuplicatePair>>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/duplicate-pairs`,
    method: 'GET',
    params,
    ...options,
  });
};

export const answerSpaceDuplicatePair = (
  identifier: string,
  pairId: string,
  answer: DuplicatePairAnswer,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<DuplicatePairAnswerResult>> => {
  return customInstance<DuplicatePairAnswerResult>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/duplicate-pairs/${encodeURIComponent(pairId)}/${answer}`,
    method: 'POST',
    ...options,
  });
};

export const listCommunityMembers = (
  spaceId: string,
  params?: { page?: number; per_page?: number; status?: string; search?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<SpaceMemberRead, SpaceMembersListMeta>>> => {
  return customInstance<PaginatedResponse<SpaceMemberRead, SpaceMembersListMeta>>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/members`,
    method: 'GET',
    params,
    ...options,
  });
};

export const changeMemberStatus = (
  spaceId: string,
  userId: string,
  newStatus: SpaceMemberStatus,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceMemberRead>> => {
  return customInstance<SpaceMemberRead>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(userId)}/status`,
    method: 'PUT',
    // Backend reads status from query param (FastAPI Query(...)), NOT request body
    params: { status: newStatus },
    ...options,
  });
};

/** Approve every pending request in a Space (optionally only PIN-holders) up to `as_of`. */
export const bulkApproveMembers = (
  spaceId: string,
  body: BulkApproveMembersBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<BulkApproveMembersResult>> => {
  return customInstance<BulkApproveMembersResult>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/members/bulk-approve`,
    method: 'POST',
    data: body,
    ...options,
  });
};

/**
 * Set (or clear, with `text: null`) the caller's own hat in a space.
 *
 * Unlike changeMemberRole/changeMemberStatus above, this takes a JSON body —
 * the backend reads a `HatSet` model, not a Query param. There is deliberately
 * no userId argument: the `/members/me/hat` path makes self-edit-only
 * structural, so no client can address another member's hat.
 */
export const setMyMemberHat = (
  spaceId: string,
  body: { text: string | null },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceMemberRead>> => {
  return customInstance<SpaceMemberRead>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/members/me/hat`,
    method: 'PUT',
    data: body,
    ...options,
  });
};

export const changeMemberRole = (
  spaceId: string,
  userId: string,
  newRole: SpaceMemberRole,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceMemberRead>> => {
  return customInstance<SpaceMemberRead>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(userId)}/role`,
    method: 'PUT',
    // Backend reads role from query param (FastAPI Query(...)), NOT request body
    params: { role: newRole },
    ...options,
  });
};

export const removeMember = (
  spaceId: string,
  userId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/members/${encodeURIComponent(userId)}`,
    method: 'DELETE',
    ...options,
  });
};

// ── Space Invitations ──

export const sendSpaceInvitations = (
  identifier: string,
  body: SpaceInviteBulkRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceInviteBulkResponse>> => {
  return customInstance<SpaceInviteBulkResponse>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/invitations`,
    method: 'POST',
    data: body,
    ...options,
  });
};

export const getSpaceInvitation = (
  identifier: string,
  token: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceInvitationDetails>> => {
  return customInstance<SpaceInvitationDetails>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/invitations`,
    method: 'GET',
    params: { token },
    ...options,
  });
};

export const acceptSpaceInvitation = (
  identifier: string,
  body: SpaceInvitationActionRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceMemberRead>> => {
  return customInstance<SpaceMemberRead>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/invitations/accept`,
    method: 'POST',
    data: body,
    ...options,
  });
};

export const declineSpaceInvitation = (
  identifier: string,
  body: SpaceInvitationActionRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/spaces/${encodeURIComponent(identifier)}/invitations/decline`,
    method: 'POST',
    data: body,
    ...options,
  });
};

export const getCommunityPage = (
  spaceId: string,
  pageType: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpacePageRead>> => {
  return customInstance<SpacePageRead>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/pages/${encodeURIComponent(pageType)}`,
    method: 'GET',
    ...options,
  });
};

export const upsertCommunityPage = (
  spaceId: string,
  pageType: string,
  data: SpacePageUpdate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpacePageRead>> => {
  return customInstance<SpacePageRead>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/pages/${encodeURIComponent(pageType)}`,
    method: 'PUT',
    data,
    ...options,
  });
};

export const listSpaceQuestions = (
  spaceId: string,
  params?: { cursor?: number; page?: number; per_page?: number; topic_ids?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<QuestionRead>>> => {
  return customInstance<PaginatedResponse<QuestionRead>>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/questions`,
    method: 'GET',
    params,
    ...options,
  });
};

export const listSpaceEvents = (
  spaceId: string,
  params?: {
    page?: number;
    per_page?: number;
    view?: EventViewParam;
    pov?: 'upcoming' | 'past';
    /** Also return events the Space lists without hosting, each carrying its own main `space`. */
    include_listed?: boolean;
  },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<EventRead>>> => {
  return customInstance<PaginatedResponse<EventRead>>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/events`,
    method: 'GET',
    params,
    ...options,
  });
};

/** Admin of the Space: puts a public event on its calendar without hosting it. Idempotent, 204. */
export const showEventOnSpaceCalendar = (
  spaceIdentifier: string,
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/listings/${encodeURIComponent(eventId)}`,
    method: 'PUT',
    ...options,
  });
};

/** Admin of the Space: takes a listed event off its calendar. Idempotent, 204. */
export const removeEventFromSpaceCalendar = (
  spaceIdentifier: string,
  eventId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/listings/${encodeURIComponent(eventId)}`,
    method: 'DELETE',
    ...options,
  });
};

export const getMyCalendars = (
  params?: { page?: number; per_page?: number; search?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<EventRead>>> => {
  return customInstance<PaginatedResponse<EventRead>>({
    url: '/api/v1/events/my-calendars',
    method: 'GET',
    params,
    ...options,
  });
};

/**
 * Meetverse live pill row source, in TWO call shapes that tile the timeline
 * between them (Dexter's split-fetch ruling 2026-07-27; backend #730). The row
 * fires both and merges the results:
 *
 *   1. DEFAULT (`only_upcoming` omitted) — live + starting-soon rows,
 *      most-alive first: live rows going-set size DESC, then soon rows
 *      soonest-first. Server-ordered; do NOT re-sort.
 *   2. `only_upcoming: true` — ONLY the upcoming band: rows whose start is
 *      strictly beyond the soon window, soonest-first, bounded server-side by
 *      the `LIVE_UPCOMING_WINDOW_DAYS` horizon lever. It never carries a live
 *      or soon row, so a busy meetverse cannot crowd the upcoming tail off the
 *      page — the reason the tail has its own call at all.
 *
 * PUBLIC endpoint (backend #625): guests get standard guest visibility with
 * host + 3 `preview_users`; authenticated viewers get their member extras and
 * up to 5 faces — never assume a fixed pile size. `per_page` ≤ 20 (server
 * default 10) on both shapes.
 *
 * Rollback truth: an older backend does not recognize `only_upcoming`, ignores
 * it, and answers call 2 with the same live+soon page as call 1 — trimmed to
 * call 2's own `per_page`. So long as call 2 asks for no MORE rows than call 1
 * (`MEETVERSE_UPCOMING_TAIL_PER_PAGE` ≤ `MEETVERSE_LIVE_PER_PAGE`, both `page:
 * 1`), its response is a strict prefix of call 1's: every id is already there,
 * the id-dedupe at `useMeetverseLiveEvents`'s merge seam contributes ZERO net
 * rows, and the row degrades to exactly its pre-split self. That dedupe is what
 * makes the degradation safe — it is required, not an optimization — and the
 * page-size ordering is what makes the dedupe total. A caller that asks for a
 * bigger upcoming page than its live page gets the live+soon overflow rendered
 * as pills on an old backend.
 */
export const getLiveEvents = (
  params?: { page?: number; per_page?: number; only_upcoming?: boolean },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<EventRead>>> => {
  return customInstance<PaginatedResponse<EventRead>>({
    url: '/api/v1/events/live',
    method: 'GET',
    params,
    ...options,
  });
};

/**
 * Live-layer recap feed: the user's GOING events whose `end_date` fell within
 * the server's recap window (`RECAP_WINDOW_HOURS` — the server default is the
 * source of truth: 48h since backend #625, mirrored client-side by
 * `LIVE_EVENTS_CONFIG.recapWindowMs`), ordered most-recently-ended first.
 * Auth-only — 401 for guests. Items also carry `preview_users` (going-set
 * faces, creator first) for the recap card.
 */
export const getMyRecaps = (
  params?: { page?: number; per_page?: number },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PaginatedResponse<EventRead>>> => {
  return customInstance<PaginatedResponse<EventRead>>({
    url: '/api/v1/events/my-recaps',
    method: 'GET',
    params,
    ...options,
  });
};

// NOTE: there is also GET /api/v1/spaces/{identifier}/events/pending which returns minimal
// metadata (name, slug, creator, timestamp) for all pending link requests in a space.
// Use it if you need a dedicated admin approval queue page beyond the inline approval below.
export const approveSpaceEvent = (
  spaceId: string,
  eventId: string,
  data: { approved: boolean },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceEventApprovalResult>> => {
  return customInstance<SpaceEventApprovalResult>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceId)}/events/${encodeURIComponent(eventId)}/approval`,
    method: 'PUT',
    data,
    ...options,
  });
};

export const useApproveSpaceEvent = (
  options?: UseMutationOptions<
    AxiosResponse<SpaceEventApprovalResult>,
    Error,
    { spaceId: string; eventId: string; approved: boolean }
  >,
) =>
  useMutation({
    mutationFn: ({ spaceId, eventId, approved }) =>
      approveSpaceEvent(spaceId, eventId, { approved }),
    ...options,
  });

export const pinQuestionInSpace = (
  spaceIdentifier: string,
  questionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PinResponse>> => {
  return customInstance<PinResponse>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/questions/${encodeURIComponent(questionId)}/pin`,
    method: 'POST',
    ...options,
  });
};

export const unpinQuestionInSpace = (
  spaceIdentifier: string,
  questionId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/questions/${encodeURIComponent(questionId)}/pin`,
    method: 'DELETE',
    ...options,
  });
};

// =============================================================================
// SPACE TOPICS API
// =============================================================================

export const getSpaceTopics = (
  spaceIdentifier: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceTopic[]>> => {
  return customInstance<SpaceTopic[]>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/topics`,
    method: 'GET',
    ...options,
  });
};

export const createSpaceTopic = (
  spaceIdentifier: string,
  data: SpaceTopicCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceTopic>> => {
  return customInstance<SpaceTopic>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/topics`,
    method: 'POST',
    data,
    ...options,
  });
};

export const updateSpaceTopic = (
  spaceIdentifier: string,
  topicId: string,
  data: SpaceTopicUpdate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceTopic>> => {
  return customInstance<SpaceTopic>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/topics/${encodeURIComponent(topicId)}`,
    method: 'PUT',
    data,
    ...options,
  });
};

export const deleteSpaceTopic = (
  spaceIdentifier: string,
  topicId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/topics/${encodeURIComponent(topicId)}`,
    method: 'DELETE',
    ...options,
  });
};

export const reorderSpaceTopics = (
  spaceIdentifier: string,
  topics: SpaceTopicReorderItem[],
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<SpaceTopic[]>> => {
  return customInstance<SpaceTopic[]>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/topics/reorder`,
    method: 'PUT',
    data: { topics },
    ...options,
  });
};

// =============================================================================
// SPACE PARTNERS API (TECH-86)
// =============================================================================

/**
 * The Partners page of a Space, trimmed for the requesting viewer on the server: visitors and
 * pending viewers get only the Anyone rows plus the two members-only counts. `token` is the
 * unlisted Space's `?invite=` token, passed exactly as `getCommunityDetail` passes it.
 */
export const getSpacePartners = (
  spaceIdentifier: string,
  params?: { token?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PartnerListingsResponse>> => {
  return customInstance<PartnerListingsResponse>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/partners`,
    method: 'GET',
    params,
    ...options,
  });
};

/** Adds a partnership (admins only). `is_first` when the Space had no live partnership before it. */
export const createSpacePartner = (
  spaceIdentifier: string,
  data: PartnerListingCreate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PartnerListingCreated>> => {
  return customInstance<PartnerListingCreated>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/partners`,
    method: 'POST',
    data,
    ...options,
  });
};

/**
 * Edits a partnership (admins only). Partial: a field sent as `null` clears it (`value`,
 * `logo_url` only). Adding or clearing the value moves the card to the end of the other section.
 */
export const updateSpacePartner = (
  spaceIdentifier: string,
  listingId: string,
  data: PartnerListingUpdate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PartnerListingRead>> => {
  return customInstance<PartnerListingRead>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/partners/${encodeURIComponent(listingId)}`,
    method: 'PUT',
    data,
    ...options,
  });
};

/** Removes a partnership for everyone (admins only; a soft delete on the server, no undo). */
export const deleteSpacePartner = (
  spaceIdentifier: string,
  listingId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return customInstance<void>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/partners/${encodeURIComponent(listingId)}`,
    method: 'DELETE',
    ...options,
  });
};

/**
 * Puts one section's partnerships in this order (admins only). `listing_ids` must be exactly the
 * section's live ids, or the server answers 409 `PARTNER_REORDER_MISMATCH`. Returns the whole
 * admin list, perks then sponsors, as the listing sends it.
 */
export const reorderSpacePartners = (
  spaceIdentifier: string,
  data: PartnerListingReorder,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PartnerListingRead[]>> => {
  return customInstance<PartnerListingRead[]>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/partners/reorder`,
    method: 'PUT',
    data,
    ...options,
  });
};

/**
 * Reports what a viewer saw and tapped on the Partners page, for the host's counts (signed in or
 * not). 202 `{ accepted }`; the server decides the viewer type, counts nothing for admins, drops
 * rows outside the Space or hidden from the caller, and dedupes per viewer per card per metric per
 * day. `token` is the unlisted Space's `?invite=` token, as `getSpacePartners` passes it. Callers
 * pass `OUTLIVE_THE_PAGE_JSON` (keepalive), which covers a batch only once fetch() is called: a
 * signed-in web viewer's batch at a tab close waits on the token lock and is lost (see that config).
 */
export const postSpacePartnerStats = (
  spaceIdentifier: string,
  data: PartnerStatsBatch,
  params?: { token?: string },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<PartnerStatsAccepted>> => {
  return customInstance<PartnerStatsAccepted>({
    url: `/api/v1/spaces/${encodeURIComponent(spaceIdentifier)}/partners/stats`,
    method: 'POST',
    data,
    params,
    ...options,
  });
};

// --- Notification Templates API ---
export const listNotificationTemplatesApiV1NotificationTemplatesGet = (
  options?: AxiosRequestConfig,
) => {
  return customInstance<NotificationTemplateRead[]>({
    url: `/api/v1/admin/notification-templates/`,
    method: 'GET',
    ...options,
  });
};

export const createNotificationTemplateApiV1NotificationTemplatesPost = (
  notificationTemplateCreate: NotificationTemplateCreate,
  options?: AxiosRequestConfig,
) => {
  return customInstance<NotificationTemplateRead>({
    url: `/api/v1/admin/notification-templates/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: notificationTemplateCreate,
    ...options,
  });
};

export const readNotificationTemplateApiV1NotificationTemplatesIdGet = (
  id: UUID,
  options?: AxiosRequestConfig,
) => {
  return customInstance<NotificationTemplateRead>({
    url: `/api/v1/admin/notification-templates/${id}`,
    method: 'GET',
    ...options,
  });
};

export const updateNotificationTemplateApiV1NotificationTemplatesIdPut = (
  id: UUID,
  notificationTemplateUpdate: NotificationTemplateUpdate,
  options?: AxiosRequestConfig,
) => {
  return customInstance<NotificationTemplateRead>({
    url: `/api/v1/admin/notification-templates/${id}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: notificationTemplateUpdate,
    ...options,
  });
};

export const deleteNotificationTemplateApiV1NotificationTemplatesIdDelete = (
  id: UUID,
  options?: AxiosRequestConfig,
) => {
  return customInstance<NotificationTemplateRead>({
    url: `/api/v1/admin/notification-templates/${id}`,
    method: 'DELETE',
    ...options,
  });
};

/**
 * One page of a template's History, newest first: one row per changed field. The route takes a
 * `per_page` of 1 to 100. A template with no logged change yet answers an empty page; a deleted
 * template keeps its History, its delete rows holding every value it had; 404 only when the
 * template is gone and nothing of it was logged. 503 `NOTIFICATION_TEMPLATE_BUSY` with
 * `Retry-After` when a lock wait or a deadlock stopped the read.
 */
export const listNotificationTemplateHistory = (
  templateId: UUID,
  params: { page: number; per_page: number },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<NotificationConfigHistoryPage>> => {
  return customInstance<NotificationConfigHistoryPage>({
    url: `/api/v1/admin/notification-templates/${templateId}/history`,
    method: 'GET',
    params,
    ...options,
  });
};

/**
 * Undoes one logged change of a template as a new logged save, from the version the page holds,
 * and answers the template as saved, through every rule a save keeps. Refused 409 when the row has
 * moved on (`CONFIG_VERSION_STALE`), when a newer change of the field came first
 * (`CONFIG_REVERT_SUPERSEDED`), when the field no longer holds the value the change wrote
 * (`CONFIG_REVERT_VALUE_CHANGED`), when there is no earlier value (`CONFIG_REVERT_NOTHING_TO_RESTORE`),
 * when the field is no longer one a save edits (`CONFIG_REVERT_FIELD_NOT_EDITABLE`) and for a
 * delete's row (`CONFIG_REVERT_DELETED`); 422 naming the field a rule refuses; a save's own 400 and
 * 409 refusals; 404 for an id that names no change of the template, and once the template is
 * deleted; 503 `NOTIFICATION_TEMPLATE_BUSY` as the History read.
 */
export const revertNotificationTemplateChange = (
  templateId: UUID,
  changeId: number,
  body: ConfigRevertRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<NotificationTemplateRead>> => {
  return customInstance<NotificationTemplateRead>({
    url: `/api/v1/admin/notification-templates/${templateId}/history/${changeId}/revert`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...options,
  });
};

// --- Notification Settings API ---
// The one global settings row (admins only). No trailing slash: the routes are mounted at `""`, so
// a slash would 307. Every route answers 503 `NOTIFICATION_SETTINGS_BUSY` with `Retry-After` when a
// lock wait or a deadlock stopped it; what held the lock lets go within seconds. That 503 is the
// server answering, so neither maintenance forwarder sends it to the detector (`isRetryLaterAnswer`).

/**
 * The settings row as stored now, with the `version` a save starts from. While the row is missing
 * the answer is the built-in values, with `source: 'fallback'` and `version` 0.
 */
export const getNotificationSettings = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<NotificationSettingsRead>> => {
  return customInstance<NotificationSettingsRead>({
    url: `/api/v1/admin/notification-settings`,
    method: 'GET',
    ...options,
  });
};

/**
 * Saves the knobs `body` names, from the version the edit started from, as one logged change, and
 * answers the row as saved. Refused 409 `CONFIG_VERSION_STALE` when another save came first, 422
 * naming the field a value or a rule refuses, and 500 `NOTIFICATION_SETTINGS_MISSING` while the row
 * is missing.
 */
export const updateNotificationSettings = (
  body: NotificationSettingsUpdate,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<NotificationSettingsRead>> => {
  return customInstance<NotificationSettingsRead>({
    url: `/api/v1/admin/notification-settings`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...options,
  });
};

/**
 * One page of the settings' History, newest first: one row per changed field. The route takes a
 * `per_page` of 1 to 100.
 */
export const listNotificationSettingsHistory = (
  params: { page: number; per_page: number },
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<NotificationConfigHistoryPage>> => {
  return customInstance<NotificationConfigHistoryPage>({
    url: `/api/v1/admin/notification-settings/history`,
    method: 'GET',
    params,
    ...options,
  });
};

/**
 * Undoes one logged change as a new logged change, from the version the page holds, and answers
 * the row as saved. Refused 409 when the row has moved on (`CONFIG_VERSION_STALE`), when a newer
 * change of the field came first (`CONFIG_REVERT_SUPERSEDED`), when the field no longer holds the
 * value the change wrote (`CONFIG_REVERT_VALUE_CHANGED`), when there is no earlier value
 * (`CONFIG_REVERT_NOTHING_TO_RESTORE`) and when the field is no longer one the page edits
 * (`CONFIG_REVERT_FIELD_NOT_EDITABLE`); 422 when the row it would leave breaks a rule; 404 for an
 * id that names no change of the settings; 500 `NOTIFICATION_SETTINGS_MISSING` while the row is
 * missing.
 */
export const revertNotificationSettingsChange = (
  changeId: number,
  body: ConfigRevertRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<NotificationSettingsRead>> => {
  return customInstance<NotificationSettingsRead>({
    url: `/api/v1/admin/notification-settings/history/${changeId}/revert`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...options,
  });
};

// --- Notification Topics API ---
/**
 * The topic taxonomy every notification is filed under (TECH-333). Member-facing and not
 * personalised, so any authenticated caller gets the same groups and topics, pre-ordered.
 *
 * No trailing slash, matching `/api/v1/notification-preferences`: the backend route is mounted at
 * `""` precisely so this call does not 307 on every load.
 */
export const getNotificationTopics = (options?: AxiosRequestConfig) => {
  return customInstance<NotificationTopicsResponse>({
    url: `/api/v1/notification-topics`,
    method: 'GET',
    ...options,
  });
};

// ── Analytics (space + platform) ──────────────────────────────────────────────
// Hand-authored fetchers for the analytics dashboards. Response types live in
// `@models/api`; cache keys in `@lib/cache/space-analytics` + `@lib/cache/admin`;
// React Query hooks in `@hooks/useSpaceAnalytics` + `@hooks/useAdminAnalytics`.
export type SpaceAnalyticsTotals = import('@models/api').SpaceAnalyticsTotals;
export type SpaceAnalyticsDailyItem = import('@models/api').SpaceAnalyticsDailyItem;
export type GlobalTotals = import('@models/api').GlobalTotals;
export type DailyAnalytics = import('@models/api').DailyAnalytics;
export type AnalyticsWindow = import('@models/api').AnalyticsWindow;
export type MeetverseGraph = import('@models/api').MeetverseGraph;

export const getSpaceAnalyticsTotals = (spaceId: string, options?: AxiosRequestConfig) => {
  return customInstance<SpaceAnalyticsTotals>({
    url: `/api/v1/spaces/${spaceId}/analytics/totals`,
    method: 'GET',
    ...options,
  });
};

export const getSpaceAnalyticsDaily = (
  spaceId: string,
  days: AnalyticsWindow,
  options?: AxiosRequestConfig,
) => {
  return customInstance<SpaceAnalyticsDailyItem[]>({
    url: `/api/v1/spaces/${spaceId}/analytics/daily`,
    method: 'GET',
    params: { days },
    ...options,
  });
};

export const getGlobalTotals = (options?: AxiosRequestConfig) => {
  return customInstance<GlobalTotals>({
    url: '/api/v1/admin/analytics/global-totals',
    method: 'GET',
    ...options,
  });
};

export const getDailyAnalytics = (days: AnalyticsWindow, options?: AxiosRequestConfig) => {
  return customInstance<DailyAnalytics[]>({
    url: '/api/v1/admin/analytics/daily',
    method: 'GET',
    params: { days },
    ...options,
  });
};

export const getMeetverseGraph = (options?: AxiosRequestConfig) => {
  return customInstance<MeetverseGraph>({
    url: '/api/v1/admin/analytics/meetverse',
    method: 'GET',
    ...options,
  });
};
